// src/components/TravelAdmin.tsx
// Admin completo para la Agencia de Viajes — CRUD via Django MCP backend.
// Design system: Orion glass-morphism (o-card, o-btn-ghost, --acc1/2/3, white/N).

import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
// 🔒 SINGLE-SOURCE · ai-agents:do-not-duplicate — formulario único de alta manual de reserva
import { RegistrarVenta } from '@/modules/sales-desk/components/RegistrarVenta';
import { useModules } from '@/context/ModulesContext';
import {
  getPermissions, listTrips, listCategories, listPlans, listAddons, listBlogs, listAuthors,
  getCompany, clearJwtCache,
  createTrip, updateTrip, deleteTrip,
  createCategory, updateCategory, deleteCategory,
  createPlan, updatePlan, deletePlan,
  createAddon, updateAddon, deleteAddon,
  createBlog, updateBlog, deleteBlog, getBlog,
  createAuthor, updateAuthor, deleteAuthor,
  updateCompany, uploadCompanyLogo, deleteCompanyLogo,
  uploadCompanyFavicon, deleteCompanyFavicon,
  getEmailConfig, updateEmailConfig, testEmailConfig,
  getStripeConfig, updateStripeConfig,
  getMetaConfig, updateMetaConfig,
  getThirdPartyKeys, updateThirdPartyKeys,
  // Sub-resource endpoints
  getTripOverview, updateTripOverview,
  listItinerary, createItineraryItem, updateItineraryItem, deleteItineraryItem,
  listFaqs, createFaq, deleteFaq,
  listTripDates, createTripDate, deleteTripDate,
  listGoals, createGoal, deleteGoal,
  listInclusions, createInclusion, deleteInclusion,
  listTripMedia, uploadTripMedia, deleteTripMedia, listGalleryImages, type GalleryImg,
  listTripSlots, getSlotItems, assignMediaToSlot, removeMediaFromSlot,
  listPlanPrices, createPlanPrice, deletePlanPrice,
  listPlanFeatures, createPlanFeature, deletePlanFeature,
  listAddonPrices, createAddonPrice, deleteAddonPrice,
  listTripAddons, deleteTripAddon,
  listDepartments, createDepartment, deleteDepartment,
  listSocialLinks, createSocialLink, deleteSocialLink,
  listContacts, createContact, deleteContact,
  // Bookings module
  getBookingDashboard, listBookings, getBooking, cancelBooking, refundBooking, resendBookingEmail,
  listBookingsByPhone, getBookingAccount, createBookingPayment, addBookingAddon,
  // Types
  type Capability, type Trip, type Category, type Plan, type Addon,
  type BlogPost, type Author, type Company,
  type TripOverview as TripOverviewT, type ItineraryItem, type FaqItem,
  type TripDate, type GoalItem, type InclusionItem, type MediaItem,
  type SlotStatus, type PlanPrice, type PlanFeature, type AddonPrice, type TripAddon,
  type Department, type SocialLink, type CompanyContact, type SlotItem,
  type BookingDashboard, type BookingListItem, type BookingDetail,
  type EmailConfig, type StripeConfig, type MetaConfig,
  type ThirdPartyKeys,
} from '@/lib/api_travel';
import {
  BarChart, Bar, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';
import {
  Loader, AlertCircle, Plane, MapPin, Users, Clock, Tag, Package, BookOpen,
  Wrench, Search, ChevronDown, ChevronRight, Shield, Star, Filter, RotateCw,
  Plus, Pencil, Trash2, X, Save, Building2, UserCircle, DollarSign,
  ListOrdered, Calendar, Image, HelpCircle, Target, CheckCircle2, Upload,
  FileText, Globe, Phone, Mail, Link2, Eye, BarChart3, TrendingUp,
  CreditCard, UserCheck, XCircle, RefreshCw, Send, MessageSquare,
  ShoppingCart, Palette, Key, Shuffle, Eraser, ExternalLink,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { WhatsAppGlyph } from '@/modules/whatsapp-app/components/WhatsAppGlyph';
import { WhatsAppWelcomeSettings } from '@/modules/whatsapp-app/components/WelcomeSettings';
import { audit } from '@/lib/audit';
import { MetaConnectPanel } from '@/components/meta/MetaConnectPanel';
import { WhatsAppCoexistenceConnect } from '@/components/meta/WhatsAppCoexistenceConnect';
import { LLMProvidersSection } from '@/components/LLMProvidersPage';
import { TripsStudio } from '@/components/travel/TripsStudio';
import {
  QuetzalBanner, QuetzalVoiceWidget, quetzalRelabel,
} from '@/components/QuetzalShell';

/* ═══════════════════════════════════════════════════════════════
   Utils
   ═══════════════════════════════════════════════════════════════ */

/** Genera un slug URL-safe a partir de texto. e.g. "Paddle Board Cañón" → "paddle-board-canon" */
function slugify(text: string): string {
  return text
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '') // quita acentos
    .toLowerCase().trim()
    .replace(/[^a-z0-9]+/g, '-')  // no alfanum → guion
    .replace(/^-+|-+$/g, '');     // trim guiones
}

/* ═══════════════════════════════════════════════════════════════
   Shared UI primitives — Orion Design System
   ═══════════════════════════════════════════════════════════════ */

function Badge({ children, variant = 'default' }: {
  children: React.ReactNode;
  variant?: 'success' | 'error' | 'warn' | 'info' | 'default' | 'accent';
}) {
  const s: Record<string, string> = {
    success: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/25',
    error:   'bg-red-500/15 text-red-400 border-red-500/25',
    warn:    'bg-amber-500/15 text-amber-400 border-amber-500/25',
    info:    'bg-sky-500/15 text-sky-400 border-sky-500/25',
    accent:  'bg-[rgba(var(--acc2-rgb),.15)] text-[var(--acc2)] border-[rgba(var(--acc2-rgb),.25)]',
    default: 'bg-white/10 text-white/50 border-white/10',
  };
  return <span className={`inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium border ${s[variant]}`}>{children}</span>;
}

function StatCard({ icon, label, value, sub, accent }: { icon: React.ReactNode; label: string; value: string | number; sub?: string; accent?: string }) {
  return (
    <div className="o-card flex items-start gap-3 hover:translate-y-[-1px] transition-transform">
      <div className="p-2 rounded-lg shrink-0" style={{ background: `rgba(var(${accent || '--acc1-rgb'}),.15)`, color: `var(${accent ? accent.replace('-rgb', '') : '--acc1'})` }}>{icon}</div>
      <div className="min-w-0">
        <p className="text-[10px] text-white/40 uppercase tracking-widest font-semibold">{label}</p>
        <p className="text-xl font-bold text-white mt-0.5">{value}</p>
        {sub && <p className="text-[11px] text-white/30 mt-0.5">{sub}</p>}
      </div>
    </div>
  );
}

function PhysicalLevel({ level }: { level: number }) {
  return (
    <div className="flex items-end gap-0.5" title={`Nivel físico: ${level}/5`}>
      {[1, 2, 3, 4, 5].map((i) => (
        <div key={i} className="w-1.5 rounded-sm transition-colors" style={{ height: `${6 + i * 3}px`, background: i <= level ? 'var(--acc1)' : 'rgba(255,255,255,.10)' }} />
      ))}
    </div>
  );
}

function EmptyState({ icon, text, className = '' }: { icon: React.ReactNode; text: string; className?: string }) {
  return <div className={`text-center py-14 text-white/20 ${className}`}><div className="mx-auto mb-3 opacity-40">{icon}</div><p>{text}</p></div>;
}

/** Orion-styled action button */
function ActionBtn({ icon, label, onClick, variant = 'default', small, disabled }: {
  icon: React.ReactNode; label?: string; onClick: () => void; variant?: 'default' | 'danger' | 'accent'; small?: boolean; disabled?: boolean;
}) {
  const base = small ? 'px-2 py-1 text-[11px]' : 'px-3 py-2 text-sm';
  const colors: Record<string, string> = {
    default: 'bg-white/5 hover:bg-white/10 border-white/10 text-white/70 hover:text-white',
    danger: 'bg-red-500/10 hover:bg-red-500/20 border-red-500/20 text-red-400 hover:text-red-300',
    accent: 'bg-[rgba(var(--acc1-rgb),.15)] hover:bg-[rgba(var(--acc1-rgb),.25)] border-[rgba(var(--acc1-rgb),.25)] text-[var(--acc1)]',
  };
  return (
    <button onClick={onClick} disabled={disabled} className={`inline-flex items-center gap-1.5 rounded-lg border transition-all ${base} ${colors[variant]} disabled:opacity-40 disabled:pointer-events-none`}>
      {icon}{label && <span>{label}</span>}
    </button>
  );
}

/* ═══════════════════════════════════════════════════════════════
   FULLSCREEN PANEL — se sobrepone a todo excepto al FAB (z-70)
   No se cierra al hacer clic fuera. Solo con botón X / Esc.
   ═══════════════════════════════════════════════════════════════ */

function Modal({ open, title, onClose, children }: {
  open: boolean; title: string; onClose: () => void; children: React.ReactNode;
}) {
  // Cerrar con Escape
  useEffect(() => {
    if (!open) return;
    const h = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [open, onClose]);

  if (!open) return null;
  return (
    // Fondo del tema (antes var(--bg-dark) fijo: en tema claro salía oscuro y, con los
    // parches de texto de .o-page, ilegible). El wrapper .o-page aplica esos parches.
    <div className="app-safe-full fixed inset-0 flex flex-col bg-background text-foreground" style={{ zIndex: 60 }}>
     <div className="o-page o-page--flush flex min-h-0 flex-1 flex-col">
      {/* Head — sticky top bar */}
      <div className="shrink-0 flex items-center justify-between px-6 py-3 border-b border-white/10"
           style={{ background: 'linear-gradient(180deg,rgba(255,255,255,.08),rgba(255,255,255,.03))' }}>
        <h3 className="text-sm font-bold text-white">{title}</h3>
        <button onClick={onClose} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-white/40 hover:text-white hover:bg-white/10 transition-colors text-xs">
          <X size={16} /> <span className="hidden sm:inline">Cerrar</span>
        </button>
      </div>
      {/* Body — scrollable content area */}
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-2xl mx-auto px-6 py-6 space-y-4 pb-24">{children}</div>
      </div>
     </div>
    </div>
  );
}

/** Styled form field */
function Field({ label, children, hint }: { label: string; children: React.ReactNode; hint?: string }) {
  return (
    <label className="block">
      <span className="text-[11px] uppercase tracking-widest text-white/30 font-semibold">{label}</span>
      {hint && <span className="text-[10px] text-white/20 ml-2">({hint})</span>}
      <div className="mt-1">{children}</div>
    </label>
  );
}

function Input({ value, onChange, placeholder, type = 'text', ...rest }: React.InputHTMLAttributes<HTMLInputElement> & { value: string | number; onChange: (e: React.ChangeEvent<HTMLInputElement>) => void }) {
  return (
    <input type={type} value={value} onChange={onChange} placeholder={placeholder} {...rest}
      className="w-full px-3 py-2 text-sm rounded-lg bg-white/5 border border-white/10 text-white placeholder:text-white/20 focus:ring-1 focus:ring-[var(--acc1)] focus:border-[var(--acc1)] outline-none transition-colors" />
  );
}

function Textarea({ value, onChange, placeholder, rows = 3 }: { value: string; onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void; placeholder?: string; rows?: number }) {
  return (
    <textarea value={value} onChange={onChange} placeholder={placeholder} rows={rows}
      className="w-full px-3 py-2 text-sm rounded-lg bg-white/5 border border-white/10 text-white placeholder:text-white/20 focus:ring-1 focus:ring-[var(--acc1)] focus:border-[var(--acc1)] outline-none transition-colors resize-y" />
  );
}

function Toggle({ checked, onChange, label }: { checked: boolean; onChange: (v: boolean) => void; label: string }) {
  return (
    <label className="inline-flex items-center gap-2 cursor-pointer">
      <button type="button" onClick={() => onChange(!checked)}
        className={`relative w-9 h-5 rounded-full transition-colors ${checked ? 'bg-[var(--acc1)]' : 'bg-white/15'}`}>
        <span className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white transition-transform ${checked ? 'translate-x-4' : ''}`} />
      </button>
      <span className="text-xs text-white/50">{label}</span>
    </label>
  );
}

function SaveBtn({ onClick, saving, label = 'Guardar' }: { onClick: () => void; saving: boolean; label?: string }) {
  return (
    <button onClick={onClick} disabled={saving}
      className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold text-white transition-all disabled:opacity-50"
      style={{ background: 'conic-gradient(from 0deg, var(--acc1), var(--acc2), var(--acc3), var(--acc1))', boxShadow: '0 8px 20px rgba(0,0,0,.25), 0 0 14px rgba(var(--acc1-rgb),.2)' }}>
      {saving ? <Loader size={14} className="animate-spin" /> : <Save size={14} />}
      {saving ? 'Guardando…' : label}
    </button>
  );
}

function DangerBtn({ onClick, label }: { onClick: () => void; label: string }) {
  return (
    <button onClick={onClick} className="w-full flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-red-500/15 border border-red-500/25 text-red-400 hover:bg-red-500/25 transition-colors">
      <Trash2 size={14} />{label}
    </button>
  );
}

/* ═══════════════════════════════════════════════════════════════
   TAB definitions
   ═══════════════════════════════════════════════════════════════ */

type TabId = 'dashboard' | 'bookings' | 'trips' | 'categories' | 'plans' | 'addons' | 'blogs' | 'authors' | 'company' | 'capabilities';
const TABS: { id: TabId; label: string; icon: React.ReactNode }[] = [
  { id: 'dashboard', label: 'Dashboard', icon: <BarChart3 size={14} /> },
  { id: 'bookings', label: 'Reservaciones', icon: <ShoppingCart size={14} /> },
  { id: 'trips', label: quetzalRelabel('Viajes'), icon: <Plane size={14} /> },
  { id: 'categories', label: 'Categorías', icon: <Tag size={14} /> },
  { id: 'plans', label: 'Planes', icon: <Package size={14} /> },
  { id: 'addons', label: 'Addons', icon: <Star size={14} /> },
  { id: 'blogs', label: 'Blog', icon: <BookOpen size={14} /> },
  { id: 'authors', label: 'Autores', icon: <UserCircle size={14} /> },
  { id: 'company', label: 'Empresa', icon: <Building2 size={14} /> },
  { id: 'capabilities', label: 'Capabilities', icon: <Wrench size={14} /> },
];

/* ═══════════════════════════════════════════════════════════════
   MAIN COMPONENT
   ═══════════════════════════════════════════════════════════════ */

interface TravelAdminProps {
  addNotification?: (n: { type: 'success' | 'error' | 'info'; message: string }) => void;
  initialTab?: TabId;
}

export function TravelAdmin({ addNotification, initialTab }: TravelAdminProps) {
  // Etiqueta per-org: gobierno usa "Institución"; el resto conserva "Empresa".
  const { archetype } = useModules();
  const orgEntityLabel = archetype === 'government' ? 'Institución' : 'Empresa';
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<TabId>(initialTab || 'dashboard');

  useEffect(() => { if (initialTab) setActiveTab(initialTab); }, [initialTab]);

  const [capabilities, setCapabilities] = useState<Capability[]>([]);
  const [keyInfo, setKeyInfo] = useState<{ key_name: string; scopes: string[]; expires_in_seconds: number } | null>(null);
  const [trips, setTrips] = useState<Trip[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [plans, setPlans] = useState<Plan[]>([]);
  const [addons, setAddons] = useState<Addon[]>([]);
  const [blogs, setBlogs] = useState<BlogPost[]>([]);
  const [authors, setAuthors] = useState<Author[]>([]);
  const [company, setCompany] = useState<Company | null>(null);

  const [search, setSearch] = useState('');
  const [capScope, setCapScope] = useState<string>('all');

  const notify = useCallback((type: 'success' | 'error' | 'info', message: string) => addNotification?.({ type, message }), [addNotification]);

  const fetchAll = useCallback(async () => {
    try {
      setLoading(true); setError(null);
      const [perms, t, cat, pl, ad, bl, au, co] = await Promise.all([
        getPermissions(), listTrips(50, 0, true), listCategories(), listPlans(),
        listAddons(), listBlogs(), listAuthors(), getCompany().catch(() => null),
      ]);
      setCapabilities(perms.capabilities);
      setKeyInfo({ key_name: perms.key_name, scopes: perms.scopes, expires_in_seconds: perms.expires_in_seconds });
      setTrips(t.items); setCategories(cat.items); setPlans(pl.items);
      setAddons(ad.items); setBlogs(bl.items); setAuthors(au.items); setCompany(co);
    } catch (err: any) {
      console.error('TravelAdmin load error:', err);
      setError(err.message || 'Error al conectar con Django');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleRefresh = useCallback(async () => {
    clearJwtCache(); notify('info', 'Recargando…'); await fetchAll(); notify('success', 'Datos actualizados');
  }, [fetchAll, notify]);

  const activeTrips = trips.filter((t) => t.active).length;
  const capScopes = useMemo(() => { const s = new Set(capabilities.map((c) => c.scope.split(':')[0])); return ['all', ...Array.from(s).sort()]; }, [capabilities]);
  const filteredCaps = useMemo(() => capabilities.filter((c) => {
    const ms = capScope === 'all' || c.scope.startsWith(capScope);
    const mq = !search || c.name.toLowerCase().includes(search.toLowerCase()) || c.summary.toLowerCase().includes(search.toLowerCase());
    return ms && mq;
  }), [capabilities, capScope, search]);

  /* ─── Loading / Error ─── */
  if (loading) return (
    <div className="flex flex-col items-center justify-center p-20">
      <Loader className="w-8 h-8 animate-spin mb-3" style={{ color: 'var(--acc1)' }} />
      <p className="text-white/50">Conectando con Django MCP…</p>
    </div>
  );
  if (error) return (
    <div className="max-w-lg mx-auto mt-12">
      <div className="o-card p-8 text-center border-l-4" style={{ borderLeftColor: 'var(--urgent)' }}>
        <AlertCircle className="w-10 h-10 mx-auto mb-4" style={{ color: 'var(--urgent)' }} />
        <h3 className="text-lg font-bold text-red-400 mb-2">Error de conexión</h3>
        <p className="text-sm text-white/50 mb-5">{error}</p>
        <button onClick={() => { clearJwtCache(); fetchAll(); }} className="o-btn-ghost inline-flex items-center gap-2 px-4 py-2 text-sm"><RotateCw size={14} /> Reintentar</button>
      </div>
    </div>
  );

  return (
    <div className="max-w-7xl mx-auto space-y-6 pb-20">
      {/* QUETZAL banner — solo activo en demo mode (hostname/querystring) */}
      <QuetzalBanner />

      {/* Header */}
      {!initialTab && (
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <Plane className="w-6 h-6" style={{ color: 'var(--acc1)' }} /> {quetzalRelabel('Administrador de Viajes')}
          </h1>
          <p className="text-white/40 text-sm mt-1 ml-8">
            Django MCP — {company?.name || quetzalRelabel('Agencia de Viajes')}
            {keyInfo && <span className="ml-2 text-white/25 text-xs">· Key: <code style={{ color: 'var(--acc1)' }}>{keyInfo.key_name}</code></span>}
          </p>
        </div>
        <button onClick={handleRefresh} className="o-btn-ghost flex items-center gap-1.5 px-3 py-2 text-sm"><RotateCw size={14} /> Recargar</button>
      </header>
      )}

      {/* QUETZAL voice widget — FAB que abre VoiceChat (OpenAI Realtime, multilingüe) */}
      <QuetzalVoiceWidget />

      {/* Stats */}
      {(!initialTab || initialTab === 'dashboard') && (
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3">
        <StatCard icon={<Plane size={16} />} label="Viajes" value={trips.length} sub={`${activeTrips} activos`} accent="--acc1-rgb" />
        <StatCard icon={<Tag size={16} />} label="Categorías" value={categories.length} accent="--acc2-rgb" />
        <StatCard icon={<Package size={16} />} label="Planes" value={plans.length} accent="--acc1-rgb" />
        <StatCard icon={<Star size={16} />} label="Addons" value={addons.length} accent="--acc3-rgb" />
        <StatCard icon={<BookOpen size={16} />} label="Blog" value={blogs.length} accent="--acc2-rgb" />
        <StatCard icon={<UserCircle size={16} />} label="Autores" value={authors.length} accent="--acc1-rgb" />
        <StatCard icon={<Building2 size={16} />} label="Empresa" value={company ? '✓' : '–'} accent="--acc3-rgb" />
        <StatCard icon={<Wrench size={16} />} label="Caps" value={capabilities.length} sub={`${capScopes.length - 1} scopes`} accent="--acc1-rgb" />
      </div>
      )}

      {/* Tabs */}
      {!initialTab && (
      <div className="border-b border-white/10">
        <nav className="flex gap-1 overflow-x-auto pb-px">
          {TABS.map((tab) => (
            <button key={tab.id} onClick={() => { setActiveTab(tab.id); setSearch(''); }}
              className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium rounded-t-lg border-b-2 transition-all whitespace-nowrap
                ${activeTab === tab.id ? 'border-[var(--acc1)] text-[var(--acc1)] bg-white/5' : 'border-transparent text-white/40 hover:text-white/70 hover:bg-white/5'}`}>
              {tab.icon}{tab.label}
            </button>
          ))}
        </nav>
      </div>
      )}

      {/* Content */}
      <div className="min-h-[400px]">
        {activeTab === 'dashboard' && <BookingsDashboardPanel notify={notify} />}
        {activeTab === 'bookings' && <BookingsPanel trips={trips} notify={notify} />}
        {activeTab === 'trips' && (
          <TripsStudio
            trips={trips}
            notify={notify}
            refresh={fetchAll}
            renderSection={(id, trip) => {
              switch (id) {
                case 'fotos': return <SubMedia tripId={trip.id} notify={notify} />;
                case 'precios': return <SubPrices tripId={trip.id} notify={notify} />;
                case 'incluye': return <SubInclusions tripId={trip.id} notify={notify} />;
                case 'itinerario': return <SubItinerary tripId={trip.id} days={(trip as any).duration} notify={notify} />;
                case 'fechas': return <SubDates tripId={trip.id} notify={notify} durationDays={trip.duration_unit === 'days' ? trip.duration : undefined} />;
                case 'preguntas': return <SubFaqs tripId={trip.id} notify={notify} />;
                case 'extras': return <SubAddons tripId={trip.id} notify={notify} />;
                case 'historia': return <SubOverview tripId={trip.id} notify={notify} />;
                case 'metas': return <SubGoals tripId={trip.id} notify={notify} />;
                default: return null;
              }
            }}
          />
        )}
        {activeTab === 'categories' && <CategoriesPanel categories={categories} notify={notify} refresh={fetchAll} />}
        {activeTab === 'plans' && <PlansPanel plans={plans} trips={trips} notify={notify} refresh={fetchAll} />}
        {activeTab === 'addons' && <AddonsPanel addons={addons} notify={notify} refresh={fetchAll} />}
        {activeTab === 'blogs' && <BlogsPanel blogs={blogs} trips={trips} authors={authors} notify={notify} refresh={fetchAll} />}
        {activeTab === 'authors' && <AuthorsPanel authors={authors} notify={notify} refresh={fetchAll} />}
        {activeTab === 'company' && <CompanyPanel company={company} notify={notify} refresh={fetchAll} />}
        {activeTab === 'capabilities' && <CapabilitiesPanel capabilities={filteredCaps} totalCount={capabilities.length} search={search} setSearch={setSearch} capScope={capScope} setCapScope={setCapScope} scopes={capScopes} />}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   TRIPS PANEL — full CRUD
   ═══════════════════════════════════════════════════════════════ */

/* ═══════════════════════════════════════════════════════════════
   TRIP SUB-TAB COMPONENTS
   ═══════════════════════════════════════════════════════════════ */

type SubProps = { tripId: number; notify: (t: any, m: string) => void };
/** SubDates necesita la duración para mostrar el RANGO real de la experiencia
 *  (un viaje de 2 días que sale el sábado corre sábado Y domingo). */
type SubDatesProps = SubProps & { durationDays?: number };

const WEEKDAYS = [
  { i: 1, label: 'L' }, { i: 2, label: 'M' }, { i: 3, label: 'X' },
  { i: 4, label: 'J' }, { i: 5, label: 'V' }, { i: 6, label: 'S' }, { i: 0, label: 'D' },
];

/** Fechas (YYYY-MM-DD) entre desde..hasta que caen en los weekdays elegidos. */
function generateDateRange(desde: string, hasta: string, weekdays: number[]): string[] {
  if (!desde || !hasta || weekdays.length === 0) return [];
  const out: string[] = [];
  // Se construye en UTC para que el día de la semana no se corra por zona horaria.
  const cur = new Date(`${desde}T12:00:00Z`);
  const end = new Date(`${hasta}T12:00:00Z`);
  if (isNaN(+cur) || isNaN(+end) || cur > end) return [];
  let guard = 0;
  while (cur <= end && guard++ < 800) {
    if (weekdays.includes(cur.getUTCDay())) out.push(cur.toISOString().slice(0, 10));
    cur.setUTCDate(cur.getUTCDate() + 1);
  }
  return out;
}

/** Fecha final de la experiencia = salida + (duración - 1) días. */
function endDateOf(startISO: string, durationDays?: number): string | null {
  if (!durationDays || durationDays <= 1) return null;
  const d = new Date(`${startISO}T12:00:00Z`);
  if (isNaN(+d)) return null;
  d.setUTCDate(d.getUTCDate() + (durationDays - 1));
  return d.toISOString().slice(0, 10);
}

const fmtCorto = (iso: string) =>
  new Date(`${iso}T12:00:00Z`).toLocaleDateString('es-MX', {
    weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC',
  });

/** Overview tab — concept & story editor */
function SubOverview({ tripId, notify }: SubProps) {
  const [data, setData] = useState<TripOverviewT>({ concept: '', story: '' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setLoading(true);
    getTripOverview(tripId).then(d => setData(d)).catch(() => {}).finally(() => setLoading(false));
  }, [tripId]);

  const handleSave = async () => {
    try { setSaving(true); await updateTripOverview(tripId, data); notify('success', 'Overview actualizado'); }
    catch (e: any) { notify('error', e.message); } finally { setSaving(false); }
  };

  if (loading) return <p className="text-white/30 text-sm py-4 flex items-center gap-2"><Loader size={14} className="animate-spin" />Cargando…</p>;
  return (
    <div className="space-y-4">
      <Field label="Concepto"><Textarea value={data.concept} onChange={e => setData(p => ({ ...p, concept: e.target.value }))} rows={3} placeholder="Concepto principal del viaje…" /></Field>
      <Field label="Historia"><Textarea value={data.story} onChange={e => setData(p => ({ ...p, story: e.target.value }))} rows={6} placeholder="Narra la experiencia del viaje…" /></Field>
      <SaveBtn onClick={handleSave} saving={saving} label="Guardar Overview" />
    </div>
  );
}

/** Itinerary tab — CRUD list */
function SubItinerary({ tripId, days, notify }: SubProps & { days?: number }) {
  const [items, setItems] = useState<ItineraryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [draft, setDraft] = useState({ day: 1, time: '', title: '', description: '', type_activity: '' });
  const [saving, setSaving] = useState(false);
  // Alta de actividad por día (varias por día): qué día está abierto + su form.
  const [addingDay, setAddingDay] = useState<number | null>(null);
  const [addForm, setAddForm] = useState({ time: '', title: '', description: '', type_activity: '' });
  // Días extra que el admin agrega más allá de la duración del viaje.
  const [extraDays, setExtraDays] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await listItinerary(tripId); setItems(Array.isArray(r) ? r : (r as any).items ?? (r as any).results ?? []); }
    catch {} finally { setLoading(false); }
  }, [tripId]);
  useEffect(() => { load(); }, [load]);

  // Cuántos días mostrar: duración del viaje, o el mayor día usado, + extra.
  const maxItemDay = items.reduce((m, it) => Math.max(m, it.day), 0);
  const numDays = Math.max(1, days || 0, maxItemDay) + extraDays;
  const byDay = new Map<number, ItineraryItem[]>();
  items.forEach(it => { const a = byDay.get(it.day) ?? []; a.push(it); byDay.set(it.day, a); });

  const addActivity = async (day: number) => {
    if (!addForm.title.trim()) { notify('error', 'Título obligatorio'); return; }
    const order = (byDay.get(day) ?? []).length;
    try {
      await createItineraryItem(tripId, { ...addForm, day, order });
      notify('success', 'Actividad agregada');
      setAddForm({ time: '', title: '', description: '', type_activity: '' });
      setAddingDay(null);
      await load();
    } catch (e: any) { notify('error', e.message); }
  };
  const remove = async (id: number) => {
    if (!confirm('¿Eliminar actividad?')) return;
    try { await deleteItineraryItem(tripId, id); notify('success', 'Eliminada'); await load(); }
    catch (e: any) { notify('error', e.message); }
  };
  const startEdit = (it: ItineraryItem) => {
    setEditingId(it.id);
    setDraft({ day: it.day, time: it.time ?? '', title: it.title ?? '', description: it.description ?? '', type_activity: it.type_activity ?? '' });
  };
  const saveEdit = async (id: number) => {
    if (!draft.title.trim()) { notify('error', 'Título obligatorio'); return; }
    setSaving(true);
    try { await updateItineraryItem(tripId, id, draft); notify('success', 'Actualizada'); setEditingId(null); await load(); }
    catch (e: any) { notify('error', e.message); } finally { setSaving(false); }
  };

  return (
    <div className="space-y-4">
      <p className="text-xs text-white/40 leading-relaxed">
        El itinerario se separa por <b className="text-white/70">día</b>. Agrega las actividades de cada día;
        la <b className="text-white/70">hora es opcional</b>.
      </p>
      {loading && <p className="text-white/30 text-sm flex items-center gap-2"><Loader size={14} className="animate-spin" />Cargando…</p>}

      {Array.from({ length: numDays }, (_, i) => i + 1).map(day => {
        const dayItems = (byDay.get(day) ?? []).slice().sort((a, b) => ((a.order ?? 0) - (b.order ?? 0)) || (a.id - b.id));
        return (
          <div key={day} className="rounded-lg border border-white/10 bg-white/[.02] p-3 space-y-2">
            <div className="flex items-center gap-2">
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold" style={{ background: 'rgba(var(--acc1-rgb),.15)', color: 'var(--acc1)' }}>
                <Calendar size={11} /> Día {day}
              </span>
              <span className="text-white/25 text-[11px]">{dayItems.length} actividad{dayItems.length === 1 ? '' : 'es'}</span>
            </div>

            {dayItems.map(it => editingId === it.id ? (
              <div key={it.id} className="rounded-lg border p-2.5 space-y-2" style={{ borderColor: 'rgba(var(--acc1-rgb),.4)', background: 'rgba(var(--acc1-rgb),.05)' }}>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  <Input type="time" value={draft.time} onChange={e => setDraft(d => ({ ...d, time: e.target.value }))} placeholder="Hora" />
                  <Input value={draft.title} onChange={e => setDraft(d => ({ ...d, title: e.target.value }))} placeholder="Título" />
                  <Input value={draft.type_activity} onChange={e => setDraft(d => ({ ...d, type_activity: e.target.value }))} placeholder="Tipo" />
                  <Input type="number" min={1} value={draft.day} onChange={e => setDraft(d => ({ ...d, day: Math.max(1, +e.target.value) }))} placeholder="Día" />
                </div>
                <Input value={draft.description} onChange={e => setDraft(d => ({ ...d, description: e.target.value }))} placeholder="Descripción…" />
                <div className="flex gap-2">
                  <button onClick={() => saveEdit(it.id)} disabled={saving} className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium disabled:opacity-50" style={{ background: 'rgba(var(--acc1-rgb),.18)', color: 'var(--acc1)' }}>{saving ? <Loader size={12} className="animate-spin" /> : <Save size={12} />}Guardar</button>
                  <button onClick={() => setEditingId(null)} disabled={saving} className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium bg-white/5 border border-white/10 text-white/60 hover:bg-white/10 disabled:opacity-50"><X size={12} />Cancelar</button>
                </div>
              </div>
            ) : (
              <div key={it.id} className="rounded-lg border border-white/8 bg-white/[.03] p-2.5 flex gap-3 items-start group">
                <div className="shrink-0 w-14 pt-0.5 text-center">
                  {it.time ? <span className="inline-flex items-center gap-0.5 text-xs font-semibold text-white/70"><Clock size={11} />{it.time}</span> : <span className="text-white/20 text-[10px]">—</span>}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-white text-sm font-medium">{it.title}</p>
                  {it.description && <p className="text-white/40 text-xs mt-0.5">{it.description}</p>}
                  {it.type_activity && <Badge variant="accent" className="mt-1">{it.type_activity}</Badge>}
                </div>
                <div className="shrink-0 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button onClick={() => startEdit(it)} title="Editar" className="p-1.5 rounded-md border" style={{ background: 'rgba(var(--acc1-rgb),.10)', borderColor: 'rgba(var(--acc1-rgb),.25)', color: 'var(--acc1)' }}><Pencil size={12} /></button>
                  <button onClick={() => remove(it.id)} title="Eliminar" className="p-1.5 rounded-md bg-red-500/10 border border-red-500/20 text-red-400 hover:bg-red-500/20"><Trash2 size={12} /></button>
                </div>
              </div>
            ))}

            {addingDay === day ? (
              <div className="rounded-lg border border-dashed border-white/15 p-2.5 space-y-2">
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  <Input type="time" value={addForm.time} onChange={e => setAddForm(f => ({ ...f, time: e.target.value }))} placeholder="Hora (opcional)" />
                  <Input value={addForm.title} onChange={e => setAddForm(f => ({ ...f, title: e.target.value }))} placeholder="Título" />
                  <Input value={addForm.type_activity} onChange={e => setAddForm(f => ({ ...f, type_activity: e.target.value }))} placeholder="Tipo" />
                </div>
                <Input value={addForm.description} onChange={e => setAddForm(f => ({ ...f, description: e.target.value }))} placeholder="Descripción…" />
                <div className="flex gap-2">
                  <button onClick={() => addActivity(day)} className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium" style={{ background: 'rgba(var(--acc1-rgb),.18)', color: 'var(--acc1)' }}><Plus size={12} />Agregar</button>
                  <button onClick={() => setAddingDay(null)} className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium bg-white/5 border border-white/10 text-white/60 hover:bg-white/10"><X size={12} />Cancelar</button>
                </div>
              </div>
            ) : (
              <button onClick={() => { setAddingDay(day); setAddForm({ time: '', title: '', description: '', type_activity: '' }); }} className="w-full py-1.5 rounded-lg border border-dashed border-white/12 text-white/40 hover:text-white/70 hover:border-white/25 text-xs flex items-center justify-center gap-1"><Plus size={12} />Actividad al Día {day}</button>
            )}
          </div>
        );
      })}

      <button onClick={() => setExtraDays(e => e + 1)} className="w-full py-2 rounded-lg border border-dashed border-white/15 text-white/50 hover:text-white/80 text-xs flex items-center justify-center gap-1"><Plus size={13} />Agregar día</button>
    </div>
  );
}


/** Adicionales del viaje — CRUD + precio.
 *  ⚠️ El backend tiene un hueco: POST /addons crea el adicional SIN vincularlo
 *  a ningún viaje, pero todos los GET/PUT filtran por trip_addons__trip__org_id
 *  → el adicional queda HUÉRFANO e invisible (36 de 52 en prod estaban así).
 *  Por eso aquí SIEMPRE se hace crear + vincular como una sola operación. */
function SubAddons({ tripId, notify }: SubProps) {
  const [items, setItems] = useState<Addon[]>([]);
  const [links, setLinks] = useState<TripAddon[]>([]);
  const [prices, setPrices] = useState<Record<number, AddonPrice[]>>({});
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ name: '', description: '', category: 'other', price: '' });
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [draft, setDraft] = useState({ name: '', description: '', category: 'other' });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [allAddons, allLinks] = await Promise.all([listAddons(), listTripAddons()]);
      // La API pagina como { items, count }: hay que leer .items. El bug era usar
      // .results (inexistente) → caía al objeto y `.filter` reventaba
      // ("(N.results ?? N ?? []).filter is not a function"). Patrón robusto:
      // items → results (legacy) → arreglo directo → vacío.
      const asArray = <T,>(r: any): T[] =>
        (r?.items ?? r?.results ?? (Array.isArray(r) ? r : [])) as T[];
      const addons = asArray<Addon>(allAddons);
      const tas = asArray<TripAddon>(allLinks);
      const mine = tas.filter(l => l.trip_id === tripId);
      const ids = new Set(mine.map(l => l.addon_id));
      const forTrip = (addons as Addon[]).filter(a => ids.has(a.id));
      setLinks(mine);
      setItems(forTrip);
      const pr: Record<number, AddonPrice[]> = {};
      await Promise.all(forTrip.map(async a => {
        try { pr[a.id] = await listAddonPrices(a.id); } catch { pr[a.id] = []; }
      }));
      setPrices(pr);
    } catch (e: any) { notify('error', e.message); }
    finally { setLoading(false); }
  }, [tripId, notify]);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!form.name.trim()) { notify('error', 'Nombre obligatorio'); return; }
    setSaving(true);
    try {
      // El backend crea Y VINCULA en una sola transacción (fix de los huérfanos):
      // sin trip_id no hay addon, y si falla el vínculo se revierte todo.
      const created = await createAddon({
        trip_id: tripId,
        name: form.name, description: form.description,
        category: form.category || 'other', available: true, active: true,
      });
      // 3) precio opcional
      const p = parseFloat(form.price);
      if (!isNaN(p) && p > 0) {
        const hoy = new Date().toISOString().slice(0, 10);
        const fin = new Date(Date.now() + 3 * 365 * 864e5).toISOString().slice(0, 10);
        await createAddonPrice(created.id, {
          price: String(p), currency: 'MXN', valid_from: hoy, valid_to: fin,
        } as any);
      }
      notify('success', 'Adicional agregado');
      setForm({ name: '', description: '', category: 'other', price: '' });
      await load();
    } catch (e: any) { notify('error', e.message); }
    finally { setSaving(false); }
  };

  const startEdit = (a: Addon) => {
    setEditingId(a.id);
    setDraft({ name: a.name, description: a.description ?? '', category: a.category ?? 'other' });
  };
  const saveEdit = async (id: number) => {
    if (!draft.name.trim()) { notify('error', 'Nombre obligatorio'); return; }
    setSaving(true);
    try { await updateAddon(id, draft); notify('success', 'Actualizado'); setEditingId(null); await load(); }
    catch (e: any) { notify('error', e.message); }
    finally { setSaving(false); }
  };
  const remove = async (a: Addon) => {
    if (!confirm(`¿Quitar «${a.name}» de este viaje?`)) return;
    try {
      const link = links.find(l => l.addon_id === a.id);
      if (link) await deleteTripAddon(link.id);
      await deleteAddon(a.id);
      notify('success', 'Eliminado'); await load();
    } catch (e: any) { notify('error', e.message); }
  };

  const precioDe = (id: number) => {
    const p = prices[id]?.[0];
    return p ? `$${parseFloat(p.price).toLocaleString('es-MX')} ${p.currency}` : 'Sin precio';
  };

  return (
    <div className="space-y-3">
      {loading && <p className="text-white/30 text-sm flex items-center gap-2"><Loader size={14} className="animate-spin" />Cargando…</p>}

      {items.map(a => (
        editingId === a.id ? (
          <div key={a.id} className="rounded-lg border p-3 space-y-2" style={{ borderColor: 'rgba(var(--acc1-rgb),.4)', background: 'rgba(var(--acc1-rgb),.05)' }}>
            <p className="text-[10px] uppercase tracking-widest font-semibold" style={{ color: 'var(--acc1)' }}>Editando adicional</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <Input value={draft.name} onChange={e => setDraft(d => ({ ...d, name: e.target.value }))} placeholder="Nombre" />
              <Input value={draft.category} onChange={e => setDraft(d => ({ ...d, category: e.target.value }))} placeholder="Categoría" />
            </div>
            <Input value={draft.description} onChange={e => setDraft(d => ({ ...d, description: e.target.value }))} placeholder="Descripción" />
            <div className="flex gap-2">
              <button onClick={() => saveEdit(a.id)} disabled={saving} className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium disabled:opacity-50" style={{ background: 'rgba(var(--acc1-rgb),.18)', color: 'var(--acc1)' }}>
                {saving ? <Loader size={12} className="animate-spin" /> : <Save size={12} />}Guardar
              </button>
              <button onClick={() => setEditingId(null)} disabled={saving} className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium bg-white/5 border border-white/10 text-white/60 hover:bg-white/10 disabled:opacity-50"><X size={12} />Cancelar</button>
            </div>
          </div>
        ) : (
          <div key={a.id} className="rounded-lg border border-white/8 bg-white/[.03] p-3 flex gap-3 items-start group">
            <div className="shrink-0 w-7 h-7 rounded-full flex items-center justify-center" style={{ background: 'rgba(var(--acc1-rgb),.15)', color: 'var(--acc1)' }}><Package size={13} /></div>
            <div className="flex-1 min-w-0">
              <p className="text-white text-sm font-medium">{a.name}</p>
              {a.description && <p className="text-white/40 text-xs mt-0.5">{a.description}</p>}
              <div className="flex gap-2 mt-1 items-center">
                <Badge variant="accent">{precioDe(a.id)}</Badge>
                {a.category && <span className="text-white/30 text-[10px]">{a.category}</span>}
              </div>
            </div>
            <div className="shrink-0 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
              <button onClick={() => startEdit(a)} title="Editar" className="p-1.5 rounded-md border" style={{ background: 'rgba(var(--acc1-rgb),.10)', borderColor: 'rgba(var(--acc1-rgb),.25)', color: 'var(--acc1)' }}><Pencil size={12} /></button>
              <button onClick={() => remove(a)} title="Eliminar" className="p-1.5 rounded-md bg-red-500/10 border border-red-500/20 text-red-400 hover:bg-red-500/20"><Trash2 size={12} /></button>
            </div>
          </div>
        )
      ))}

      {!loading && items.length === 0 && <EmptyState icon={<Package size={24} />} text="Este viaje no tiene adicionales" />}

      <div className="rounded-lg border border-dashed border-white/15 p-3 space-y-2">
        <p className="text-[10px] uppercase tracking-widest text-white/25 font-semibold">Agregar adicional</p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          <Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Nombre" />
          <Input value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))} placeholder="Categoría" />
          <Input type="number" min={0} value={form.price} onChange={e => setForm(f => ({ ...f, price: e.target.value }))} placeholder="Precio MXN" />
          <button onClick={add} disabled={saving} className="flex items-center justify-center gap-1 px-3 py-2 rounded-lg text-xs font-medium disabled:opacity-50" style={{ background: 'rgba(var(--acc1-rgb),.15)', color: 'var(--acc1)' }}>
            {saving ? <Loader size={14} className="animate-spin" /> : <Plus size={14} />}Agregar
          </button>
        </div>
        <Input value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="Descripción del adicional…" />
      </div>
    </div>
  );
}

/** Dates tab — CRUD list */
function SubDates({ tripId, notify, durationDays }: SubDatesProps) {
  const [items, setItems] = useState<TripDate[]>([]);
  const [loading, setLoading] = useState(true);
  const [newDate, setNewDate] = useState('');
  // Generador por RANGO — el admin ya no captura fecha por fecha.
  const [desde, setDesde] = useState('');
  const [hasta, setHasta] = useState('');
  const [dias, setDias] = useState<number[]>([6]); // sábado por defecto
  const [generando, setGenerando] = useState(false);
  const [progreso, setProgreso] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await listTripDates(tripId); setItems(Array.isArray(r) ? r : (r as any).results ?? []); }
    catch {} finally { setLoading(false); }
  }, [tripId]);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!newDate) return;
    try { await createTripDate(tripId, { date: newDate, active: true }); notify('success', 'Fecha agregada'); setNewDate(''); await load(); }
    catch (e: any) { notify('error', e.message); }
  };
  const remove = async (id: number) => {
    try { await deleteTripDate(tripId, id); notify('success', 'Eliminada'); await load(); }
    catch (e: any) { notify('error', e.message); }
  };

  const toggleDia = (i: number) =>
    setDias(d => (d.includes(i) ? d.filter(x => x !== i) : [...d, i]));

  // Previsualización: cuáles se crearían (excluye las que ya existen).
  const existentes = useMemo(() => new Set(items.map(d => d.date)), [items]);
  const candidatas = useMemo(
    () => generateDateRange(desde, hasta, dias).filter(d => !existentes.has(d)),
    [desde, hasta, dias, existentes],
  );

  const generar = async () => {
    if (candidatas.length === 0) return;
    setGenerando(true); setProgreso(0);
    let ok = 0, fail = 0;
    for (let i = 0; i < candidatas.length; i++) {
      try { await createTripDate(tripId, { date: candidatas[i], active: true }); ok++; }
      catch { fail++; }
      setProgreso(Math.round(((i + 1) / candidatas.length) * 100));
    }
    setGenerando(false);
    notify(fail ? 'error' : 'success',
      `${ok} fecha(s) creada(s)${fail ? ` · ${fail} fallaron` : ''}`);
    await load();
  };

  return (
    <div className="space-y-3">
      {loading && <p className="text-white/30 text-sm flex items-center gap-2"><Loader size={14} className="animate-spin" />Cargando…</p>}
      <div className="flex flex-wrap gap-2">
        {[...items].sort((a, b) => a.date.localeCompare(b.date)).map(d => {
          const fin = endDateOf(d.date, durationDays);
          return (
            <div key={d.id} className="flex items-center gap-2 rounded-lg border border-white/10 bg-white/[.03] px-3 py-2 group">
              <Calendar size={12} className="text-white/30" />
              {/* Un viaje de 2 días que sale el sábado corre sábado Y domingo:
                  mostramos el RANGO completo, no solo la salida. */}
              <span className="text-white text-sm">
                {fmtCorto(d.date)}{fin && <span className="text-white/45"> → {fmtCorto(fin)}</span>}
              </span>
              <Badge variant={d.active ? 'success' : 'error'}>{d.active ? 'Activa' : 'Off'}</Badge>
              <button onClick={() => remove(d.id)} className="p-1 rounded text-red-400 hover:bg-red-500/20 opacity-0 group-hover:opacity-100 transition-all"><Trash2 size={11} /></button>
            </div>
          );
        })}
      </div>
      {!loading && items.length === 0 && <EmptyState icon={<Calendar size={24} />} text="Sin fechas disponibles" />}

      {/* ── Generador por rango (temporada) ── */}
      <div className="rounded-lg border border-dashed border-white/15 p-3 space-y-2">
        <p className="text-[10px] uppercase tracking-widest text-white/25 font-semibold">
          Generar temporada · sin capturar fecha por fecha
        </p>
        <div className="flex flex-wrap gap-2 items-end">
          <Field label="Desde"><input type="date" value={desde} onChange={e => setDesde(e.target.value)} className="px-3 py-2 text-sm rounded-lg bg-white/5 border border-white/10 text-white outline-none" /></Field>
          <Field label="Hasta"><input type="date" value={hasta} onChange={e => setHasta(e.target.value)} className="px-3 py-2 text-sm rounded-lg bg-white/5 border border-white/10 text-white outline-none" /></Field>
          <div>
            <p className="text-[10px] uppercase tracking-widest text-white/25 font-semibold mb-1">Días de salida</p>
            <div className="flex gap-1">
              {WEEKDAYS.map(w => (
                <button
                  key={w.i}
                  onClick={() => toggleDia(w.i)}
                  className="w-8 h-8 rounded-md text-xs font-semibold border transition-colors"
                  style={dias.includes(w.i)
                    ? { background: 'rgba(var(--acc1-rgb),.20)', borderColor: 'var(--acc1)', color: 'var(--acc1)' }
                    : { background: 'rgba(255,255,255,.04)', borderColor: 'rgba(255,255,255,.10)', color: 'rgba(255,255,255,.45)' }}
                >{w.label}</button>
              ))}
            </div>
          </div>
          <button
            onClick={generar}
            disabled={generando || candidatas.length === 0}
            className="flex items-center gap-1 px-3 py-2 rounded-lg text-xs font-medium transition-colors mb-0.5 disabled:opacity-40"
            style={{ background: 'rgba(var(--acc1-rgb),.15)', color: 'var(--acc1)' }}
          >
            {generando ? <><Loader size={14} className="animate-spin" />{progreso}%</> : <><Plus size={14} />Generar {candidatas.length || ''}</>}
          </button>
        </div>
        {desde && hasta && (
          <p className="text-[11px] text-white/35">
            {candidatas.length > 0
              ? <>Se crearán <b className="text-white/70">{candidatas.length}</b> fecha(s){durationDays && durationDays > 1 ? <> · cada salida dura <b className="text-white/70">{durationDays} días</b></> : null}. Las repetidas se omiten.</>
              : <>No hay fechas nuevas con ese rango y días (o ya existen todas).</>}
          </p>
        )}
      </div>

      {/* ── Fecha suelta (sigue disponible) ── */}
      <div className="flex gap-2 items-end">
        <Field label="Fecha suelta"><input type="date" value={newDate} onChange={e => setNewDate(e.target.value)} className="px-3 py-2 text-sm rounded-lg bg-white/5 border border-white/10 text-white outline-none" /></Field>
        <button onClick={add} className="flex items-center gap-1 px-3 py-2 rounded-lg text-xs font-medium transition-colors mb-0.5" style={{ background: 'rgba(var(--acc1-rgb),.15)', color: 'var(--acc1)' }}><Plus size={14} />Agregar</button>
      </div>
    </div>
  );
}

/** FAQs tab — CRUD list */
function SubFaqs({ tripId, notify }: SubProps) {
  const [items, setItems] = useState<FaqItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ question: '', answer: '' });

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await listFaqs(tripId); setItems(Array.isArray(r) ? r : (r as any).results ?? []); }
    catch {} finally { setLoading(false); }
  }, [tripId]);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!form.question.trim()) { notify('error', 'Pregunta obligatoria'); return; }
    try { await createFaq(tripId, { ...form, order: items.length + 1 }); notify('success', 'FAQ agregada'); setForm({ question: '', answer: '' }); await load(); }
    catch (e: any) { notify('error', e.message); }
  };
  const remove = async (id: number) => {
    try { await deleteFaq(tripId, id); notify('success', 'Eliminada'); await load(); }
    catch (e: any) { notify('error', e.message); }
  };

  return (
    <div className="space-y-3">
      {loading && <p className="text-white/30 text-sm flex items-center gap-2"><Loader size={14} className="animate-spin" />Cargando…</p>}
      {items.sort((a, b) => a.order - b.order).map(f => (
        <div key={f.id} className="rounded-lg border border-white/8 bg-white/[.03] p-3 group">
          <div className="flex items-start gap-2">
            <HelpCircle size={14} className="text-[var(--acc2)] shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-white text-sm font-medium">{f.question}</p>
              <p className="text-white/40 text-xs mt-1">{f.answer}</p>
            </div>
            <button onClick={() => remove(f.id)} className="shrink-0 p-1.5 rounded-md bg-red-500/10 border border-red-500/20 text-red-400 hover:bg-red-500/20 transition-colors opacity-0 group-hover:opacity-100"><Trash2 size={12} /></button>
          </div>
        </div>
      ))}
      {!loading && items.length === 0 && <EmptyState icon={<HelpCircle size={24} />} text="Sin FAQs" />}
      <div className="rounded-lg border border-dashed border-white/15 p-3 space-y-2">
        <p className="text-[10px] uppercase tracking-widest text-white/25 font-semibold">Agregar FAQ</p>
        <Input value={form.question} onChange={e => setForm(f => ({ ...f, question: e.target.value }))} placeholder="Pregunta" />
        <Textarea value={form.answer} onChange={e => setForm(f => ({ ...f, answer: e.target.value }))} placeholder="Respuesta" rows={2} />
        <button onClick={add} className="flex items-center gap-1 px-3 py-2 rounded-lg text-xs font-medium transition-colors" style={{ background: 'rgba(var(--acc1-rgb),.15)', color: 'var(--acc1)' }}><Plus size={14} />Agregar</button>
      </div>
    </div>
  );
}

/** Goals tab — CRUD list */
function SubGoals({ tripId, notify }: SubProps) {
  const [items, setItems] = useState<GoalItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await listGoals(tripId); setItems(Array.isArray(r) ? r : (r as any).results ?? []); }
    catch {} finally { setLoading(false); }
  }, [tripId]);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!text.trim()) return;
    try { await createGoal(tripId, { text, order: items.length + 1, active: true }); notify('success', 'Meta agregada'); setText(''); await load(); }
    catch (e: any) { notify('error', e.message); }
  };
  const remove = async (id: number) => {
    try { await deleteGoal(tripId, id); notify('success', 'Eliminada'); await load(); }
    catch (e: any) { notify('error', e.message); }
  };

  return (
    <div className="space-y-3">
      {loading && <p className="text-white/30 text-sm flex items-center gap-2"><Loader size={14} className="animate-spin" />Cargando…</p>}
      {items.sort((a, b) => a.order - b.order).map(g => (
        <div key={g.id} className="flex items-center gap-2 rounded-lg border border-white/8 bg-white/[.03] px-3 py-2 group">
          <Target size={12} className="text-[var(--acc1)] shrink-0" />
          <span className="flex-1 text-white text-sm">{g.text}</span>
          <Badge variant={g.active ? 'success' : 'error'}>{g.active ? 'Activa' : 'Off'}</Badge>
          <button onClick={() => remove(g.id)} className="p-1 rounded text-red-400 hover:bg-red-500/20 opacity-0 group-hover:opacity-100 transition-all"><Trash2 size={11} /></button>
        </div>
      ))}
      {!loading && items.length === 0 && <EmptyState icon={<Target size={24} />} text="Sin metas" />}
      <div className="flex gap-2">
        <div className="flex-1"><Input value={text} onChange={e => setText(e.target.value)} placeholder="Nueva meta…" /></div>
        <button onClick={add} className="flex items-center gap-1 px-3 py-2 rounded-lg text-xs font-medium transition-colors shrink-0" style={{ background: 'rgba(var(--acc1-rgb),.15)', color: 'var(--acc1)' }}><Plus size={14} />Agregar</button>
      </div>
    </div>
  );
}

/** Inclusions tab — CRUD list (included / excluded) */
function SubInclusions({ tripId, notify }: SubProps) {
  const [items, setItems] = useState<InclusionItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ inclusion: '', included: true });

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await listInclusions(tripId); setItems(Array.isArray(r) ? r : (r as any).results ?? []); }
    catch {} finally { setLoading(false); }
  }, [tripId]);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!form.inclusion.trim()) return;
    try { await createInclusion(tripId, form); notify('success', form.included ? 'Inclusión agregada' : 'Exclusión agregada'); setForm({ inclusion: '', included: true }); await load(); }
    catch (e: any) { notify('error', e.message); }
  };
  const remove = async (id: number) => {
    try { await deleteInclusion(tripId, id); notify('success', 'Eliminada'); await load(); }
    catch (e: any) { notify('error', e.message); }
  };

  const included = items.filter(i => i.included);
  const excluded = items.filter(i => !i.included);

  return (
    <div className="space-y-4">
      {loading && <p className="text-white/30 text-sm flex items-center gap-2"><Loader size={14} className="animate-spin" />Cargando…</p>}
      {included.length > 0 && (
        <div>
          <p className="text-[10px] uppercase tracking-widest text-green-400/60 font-semibold mb-2">✓ Incluido</p>
          <div className="space-y-1">
            {included.map(i => (
              <div key={i.id} className="flex items-center gap-2 rounded-lg border border-green-500/10 bg-green-500/5 px-3 py-2 group">
                <CheckCircle2 size={12} className="text-green-400 shrink-0" />
                <span className="flex-1 text-white text-sm">{i.inclusion}</span>
                <button onClick={() => remove(i.id)} className="p-1 rounded text-red-400 hover:bg-red-500/20 opacity-0 group-hover:opacity-100 transition-all"><Trash2 size={11} /></button>
              </div>
            ))}
          </div>
        </div>
      )}
      {excluded.length > 0 && (
        <div>
          <p className="text-[10px] uppercase tracking-widest text-red-400/60 font-semibold mb-2">✗ No incluido</p>
          <div className="space-y-1">
            {excluded.map(i => (
              <div key={i.id} className="flex items-center gap-2 rounded-lg border border-red-500/10 bg-red-500/5 px-3 py-2 group">
                <X size={12} className="text-red-400 shrink-0" />
                <span className="flex-1 text-white text-sm">{i.inclusion}</span>
                <button onClick={() => remove(i.id)} className="p-1 rounded text-red-400 hover:bg-red-500/20 opacity-0 group-hover:opacity-100 transition-all"><Trash2 size={11} /></button>
              </div>
            ))}
          </div>
        </div>
      )}
      {!loading && items.length === 0 && <EmptyState icon={<CheckCircle2 size={24} />} text="Sin inclusiones / exclusiones" />}
      <div className="rounded-lg border border-dashed border-white/15 p-3 space-y-2">
        <p className="text-[10px] uppercase tracking-widest text-white/25 font-semibold">Agregar inclusión / exclusión</p>
        <div className="flex gap-2 items-end flex-wrap">
          <div className="flex-1 min-w-[200px]"><Input value={form.inclusion} onChange={e => setForm(f => ({ ...f, inclusion: e.target.value }))} placeholder="Texto de la inclusión…" /></div>
          <Toggle checked={form.included} onChange={v => setForm(f => ({ ...f, included: v }))} label={form.included ? 'Incluido' : 'Excluido'} />
          <button onClick={add} className="flex items-center gap-1 px-3 py-2 rounded-lg text-xs font-medium transition-colors shrink-0" style={{ background: 'rgba(var(--acc1-rgb),.15)', color: 'var(--acc1)' }}><Plus size={14} />Agregar</button>
        </div>
      </div>
    </div>
  );
}

/* ── Slot display config ── */
const SLOT_LABELS: Record<string, { icon: React.ReactNode; label: string; color: string }> = {
  heroImage:    { icon: <Star size={14} />,    label: 'Hero Image',    color: 'var(--acc1)' },
  mainImage:    { icon: <Image size={14} />,   label: 'Imagen Principal', color: 'var(--acc2)' },
  visualTiles:  { icon: <Eye size={14} />,     label: 'Visual Tiles',  color: 'var(--acc3)' },
  galleryImages:{ icon: <Image size={14} />,   label: 'Galería Imágenes', color: '#60a5fa' },
  galleryVideo: { icon: <Eye size={14} />,     label: 'Galería Videos', color: '#a78bfa' },
};

/** Media tab — slot-organized with per-slot upload */
function SubMedia({ tripId, notify }: SubProps) {
  const [slots, setSlots] = useState<SlotStatus[]>([]);
  const [slotItems, setSlotItems] = useState<Record<number, SlotItem[]>>({});
  const [library, setLibrary] = useState<MediaItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploadingSlot, setUploadingSlot] = useState<number | null>(null);
  const [assigningTo, setAssigningTo] = useState<number | null>(null); // slotId for library picker
  const fileRefs = useRef<Record<number, HTMLInputElement | null>>({});
  // Pending upload state: files waiting for meta_data before uploading
  const [pendingUpload, setPendingUpload] = useState<{ slotId: number; files: File[] } | null>(null);
  const [pendingTitles, setPendingTitles] = useState<string[]>([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [sl, lib] = await Promise.all([
        listTripSlots(tripId).catch(() => []),
        listTripMedia(tripId).catch(() => []),
      ]);
      const slotsArr = Array.isArray(sl) ? sl : [];
      setSlots(slotsArr);
      setLibrary(Array.isArray(lib) ? lib : []);
      // Fetch items for each slot in parallel
      const itemsMap: Record<number, SlotItem[]> = {};
      await Promise.all(slotsArr.map(async (s) => {
        try { itemsMap[s.id] = await getSlotItems(tripId, s.id); }
        catch { itemsMap[s.id] = []; }
      }));
      setSlotItems(itemsMap);
    } catch {} finally { setLoading(false); }
  }, [tripId]);
  useEffect(() => { load(); }, [load]);

  // Upload directly to a slot
  const handleSlotUpload = async (slotId: number, e: React.ChangeEvent<HTMLInputElement>) => {
    const fileList = e.target.files;
    if (!fileList || fileList.length === 0) return;
    const files = Array.from(fileList);
    // If slot has meta_data_schema, collect titles before uploading
    const slot = slots.find(s => s.id === slotId);
    if (slot?.meta_data_schema) {
      setPendingUpload({ slotId, files });
      setPendingTitles(files.map(() => ''));
      const ref = fileRefs.current[slotId]; if (ref) ref.value = '';
      return;
    }
    try {
      setUploadingSlot(slotId);
      const result = await uploadTripMedia(tripId, files, slotId);
      const count = result.uploaded?.length || files.length;
      notify('success', count > 1 ? `${count} archivos subidos al slot` : 'Archivo subido al slot');
      await load();
    } catch (err: any) { notify('error', err.message); }
    finally { setUploadingSlot(null); const ref = fileRefs.current[slotId]; if (ref) ref.value = ''; }
  };

  // Confirm pending upload with meta_datas
  const confirmPendingUpload = async () => {
    if (!pendingUpload) return;
    const { slotId, files } = pendingUpload;
    if (pendingTitles.some(t => !t.trim())) return;
    try {
      setUploadingSlot(slotId);
      const metaDatas = pendingTitles.map(t => ({ title: t.trim() }));
      const result = await uploadTripMedia(tripId, files, slotId, metaDatas);
      const count = result.uploaded?.length || files.length;
      notify('success', count > 1 ? `${count} archivos subidos al slot` : 'Archivo subido al slot');
      setPendingUpload(null);
      setPendingTitles([]);
      await load();
    } catch (err: any) { notify('error', err.message); }
    finally { setUploadingSlot(null); }
  };

  // Remove item from slot
  const handleRemoveFromSlot = async (slotId: number, assignmentId: number) => {
    try {
      await removeMediaFromSlot(tripId, slotId, assignmentId);
      notify('success', 'Removido del slot');
      await load();
    } catch (e: any) { notify('error', e.message); }
  };

  // Assign library item to a slot
  const handleAssign = async (slotId: number, mediaId: number, metaData?: Record<string, any>) => {
    try {
      await assignMediaToSlot(tripId, slotId, { media_id: mediaId, ...(metaData ? { meta_data: metaData } : {}) });
      notify('success', 'Asignado al slot');
      setAssigningTo(null);
      await load();
    } catch (e: any) { notify('error', e.message); }
  };

  // Delete from library entirely
  const handleDeleteMedia = async (mediaId: number) => {
    if (!confirm('¿Eliminar archivo de la biblioteca? Se removerá de todos los slots.')) return;
    try { await deleteTripMedia(tripId, mediaId); notify('success', 'Eliminado'); await load(); }
    catch (e: any) { notify('error', e.message); }
  };

  // IDs of media assigned to any slot
  const assignedMediaIds = useMemo(() => {
    const ids = new Set<number>();
    Object.values(slotItems).flat().forEach(si => ids.add(si.media_id));
    return ids;
  }, [slotItems]);

  const unassignedLibrary = useMemo(() => library.filter(m => !assignedMediaIds.has(m.id)), [library, assignedMediaIds]);

  if (loading) return <div className="flex items-center gap-2 py-8 justify-center text-white/30 text-sm"><Loader size={16} className="animate-spin" />Cargando multimedia…</div>;

  return (
    <div className="space-y-6">
      {/* Slot Sections */}
      {slots.map(slot => {
        const items = slotItems[slot.id] || [];
        const cfg = SLOT_LABELS[slot.name] || { icon: <Image size={14} />, label: slot.name, color: 'var(--acc1)' };
        const isFull = items.length >= slot.max_items;
        const acceptTypes = slot.allowed_media_types?.includes('video') && !slot.allowed_media_types?.includes('image')
          ? 'video/*'
          : slot.allowed_media_types?.includes('image') && !slot.allowed_media_types?.includes('video')
            ? 'image/*'
            : 'image/*,video/*';
        const isUploading = uploadingSlot === slot.id;
        const isMulti = slot.max_items > 1;
        const hasMetaSchema = !!slot.meta_data_schema;

        return (
          <div key={slot.id} className="rounded-xl border border-white/10 bg-white/[.02] overflow-hidden">
            {/* Slot header */}
            <div className="flex items-center gap-3 px-4 py-3 border-b border-white/[.06]">
              <div className="flex items-center justify-center w-7 h-7 rounded-lg" style={{ background: `${cfg.color}20`, color: cfg.color }}>
                {cfg.icon}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-white font-semibold text-sm">{cfg.label}</span>
                  <span className="text-[10px] px-1.5 py-0.5 rounded-full" style={{ background: isFull ? 'rgba(239,68,68,.15)' : 'rgba(var(--acc1-rgb),.12)', color: isFull ? '#f87171' : 'var(--acc1)' }}>
                    {items.length}/{slot.max_items}
                  </span>
                </div>
                <p className="text-[10px] text-white/30 mt-0.5 truncate">{slot.description}</p>
              </div>
              {/* Upload button */}
              {!isFull && (
                <>
                  <input
                    ref={el => { fileRefs.current[slot.id] = el; }}
                    type="file"
                    accept={acceptTypes}
                    multiple={isMulti}
                    onChange={e => handleSlotUpload(slot.id, e)}
                    className="hidden"
                  />
                  <button onClick={() => fileRefs.current[slot.id]?.click()} disabled={isUploading}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all shrink-0 hover:scale-[1.02]"
                    style={{ background: `${cfg.color}18`, color: cfg.color }}>
                    {isUploading ? <Loader size={12} className="animate-spin" /> : <Upload size={12} />}
                    {isUploading ? 'Subiendo…' : 'Subir'}
                  </button>
                </>
              )}
              {/* Assign from library */}
              {!isFull && library.length > 0 && (
                <button onClick={() => setAssigningTo(assigningTo === slot.id ? null : slot.id)}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all shrink-0 border border-white/10 text-white/50 hover:text-white/80 hover:border-white/20">
                  <Link2 size={12} />Asignar
                </button>
              )}
            </div>

            {/* Slot items grid */}
            {items.length > 0 ? (
              <div className={`grid gap-2 p-3 ${slot.max_items === 1 ? 'grid-cols-1 max-w-[280px]' : 'grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5'}`}>
                {items.map(item => (
                  <div key={item.id} className="relative rounded-lg overflow-hidden border border-white/10 bg-white/[.03] group aspect-square">
                    {item.media_type === 'image' ? (
                      <img src={item.file} alt={item.alt_text || item.filename} className="w-full h-full object-cover" loading="lazy" />
                    ) : (
                      <video src={item.file} className="w-full h-full object-cover" muted />
                    )}
                    {/* Overlay on hover */}
                    <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center gap-1.5">
                      <p className="text-white text-[10px] truncate max-w-[90%] px-1">{item.filename}</p>
                      {item.meta_data?.title && <p className="text-[9px] px-1.5 py-0.5 rounded bg-white/10 text-white/70">{item.meta_data.title}</p>}
                      {item.width && item.height && <p className="text-white/40 text-[9px]">{item.width}×{item.height}</p>}
                      <button onClick={() => handleRemoveFromSlot(slot.id, item.id)}
                        className="mt-1 flex items-center gap-1 px-2 py-1 rounded text-[10px] text-red-400 bg-red-500/20 hover:bg-red-500/30 transition-colors">
                        <X size={10} />Quitar del slot
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="px-4 py-6 text-center text-white/20 text-xs">
                Sin archivos — {isFull ? 'Slot lleno' : 'Sube o asigna archivos arriba ↑'}
              </div>
            )}

            {/* Pending meta_data form (shown when files need titles before upload) */}
            {pendingUpload && pendingUpload.slotId === slot.id && (
              <div className="border-t border-white/[.06] bg-white/[.02] p-3 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-[11px] text-white/50">Completa los datos de cada archivo antes de subir</span>
                  <button onClick={() => { setPendingUpload(null); setPendingTitles([]); }} className="text-white/30 hover:text-white/60"><X size={14} /></button>
                </div>
                {pendingUpload.files.map((f, i) => (
                  <div key={i} className="flex items-center gap-3 p-2 rounded-lg border border-white/10 bg-white/[.03]">
                    <span className="text-[10px] text-white/40 truncate max-w-[150px] shrink-0">{f.name}</span>
                    <div className="flex-1">
                      <label className="text-[10px] text-white/40 block mb-0.5">Título (requerido)</label>
                      <input value={pendingTitles[i] || ''} onChange={e => setPendingTitles(prev => { const next = [...prev]; next[i] = e.target.value; return next; })}
                        placeholder="Título del tile…"
                        className="w-full px-2 py-1.5 rounded-md bg-white/[.06] text-white text-xs border border-white/10 focus:border-[var(--acc1)] outline-none" />
                    </div>
                  </div>
                ))}
                <div className="flex items-center gap-2 justify-end">
                  <button onClick={() => { setPendingUpload(null); setPendingTitles([]); }}
                    className="px-3 py-1.5 rounded-lg text-xs text-white/40 hover:text-white/60 transition-colors">Cancelar</button>
                  <button onClick={confirmPendingUpload} disabled={pendingTitles.some(t => !t.trim()) || uploadingSlot === slot.id}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium disabled:opacity-30 transition-colors"
                    style={{ background: `${cfg.color}18`, color: cfg.color }}>
                    {uploadingSlot === slot.id ? <Loader size={12} className="animate-spin" /> : <Upload size={12} />}
                    {uploadingSlot === slot.id ? 'Subiendo…' : 'Subir'}
                  </button>
                </div>
              </div>
            )}

            {/* Library picker (inline when "Asignar" is clicked) */}
            {assigningTo === slot.id && (
              <LibraryPicker
                library={library}
                slot={slot}
                onAssign={(mediaId, meta) => handleAssign(slot.id, mediaId, meta)}
                onClose={() => setAssigningTo(null)}
              />
            )}
          </div>
        );
      })}

      {/* Unassigned library / full library */}
      <div className="rounded-xl border border-white/10 bg-white/[.02] overflow-hidden">
        <div className="flex items-center gap-3 px-4 py-3 border-b border-white/[.06]">
          <div className="flex items-center justify-center w-7 h-7 rounded-lg bg-white/[.06] text-white/40">
            <Package size={14} />
          </div>
          <div className="flex-1">
            <span className="text-white/70 font-semibold text-sm">Biblioteca</span>
            <span className="ml-2 text-[10px] text-white/30">{library.length} total · {unassignedLibrary.length} sin asignar</span>
          </div>
        </div>
        {library.length > 0 ? (
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-2 p-3">
            {library.map(m => {
              const isAssigned = assignedMediaIds.has(m.id);
              return (
                <div key={m.id} className={`relative rounded-lg overflow-hidden border bg-white/[.03] group aspect-square ${isAssigned ? 'border-white/5 opacity-50' : 'border-white/10'}`}>
                  {m.media_type === 'image' ? (
                    <img src={m.file} alt={m.alt_text || m.original_filename} className="w-full h-full object-cover" loading="lazy" />
                  ) : (
                    <video src={m.file} className="w-full h-full object-cover" muted />
                  )}
                  {isAssigned && (
                    <div className="absolute top-1.5 right-1.5 w-5 h-5 rounded-full flex items-center justify-center" style={{ background: 'rgba(var(--acc1-rgb),.8)' }}>
                      <CheckCircle2 size={10} className="text-white" />
                    </div>
                  )}
                  <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center gap-1">
                    <p className="text-white text-[9px] truncate max-w-[90%] px-1">{m.original_filename}</p>
                    <button onClick={() => handleDeleteMedia(m.id)}
                      className="flex items-center gap-1 px-2 py-0.5 rounded text-[10px] text-red-400 bg-red-500/20 hover:bg-red-500/30 transition-colors">
                      <Trash2 size={9} />Eliminar
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="px-4 py-6 text-center text-white/20 text-xs">Biblioteca vacía — sube archivos desde los slots arriba</div>
        )}
      </div>
    </div>
  );
}

/** Library picker overlay — shown inline below a slot for assigning existing media */
function LibraryPicker({ library, slot, onAssign, onClose }: {
  library: MediaItem[];
  slot: SlotStatus;
  onAssign: (mediaId: number, meta?: Record<string, any>) => void;
  onClose: () => void;
}) {
  const [metaTitle, setMetaTitle] = useState('');
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const hasMetaSchema = !!slot.meta_data_schema;
  const acceptsVideo = slot.allowed_media_types?.includes('video');
  const acceptsImage = slot.allowed_media_types?.includes('image');

  // Filter library to only show compatible media types
  const compatible = library.filter(m => {
    if (acceptsImage && acceptsVideo) return true;
    if (acceptsImage && m.media_type === 'image') return true;
    if (acceptsVideo && m.media_type === 'video') return true;
    return false;
  });

  const doAssign = (mediaId: number) => {
    if (hasMetaSchema) {
      setSelectedId(mediaId);
      return; // Wait for meta_data form
    }
    onAssign(mediaId);
  };

  const confirmWithMeta = () => {
    if (!metaTitle.trim()) return;
    if (selectedId != null) onAssign(selectedId, { title: metaTitle });
  };

  return (
    <div className="border-t border-white/[.06] bg-white/[.02] p-3 space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-[11px] text-white/40">Selecciona de la biblioteca →</span>
        <button onClick={onClose} className="text-white/30 hover:text-white/60"><X size={14} /></button>
      </div>

      {/* Meta data form for visualTiles */}
      {hasMetaSchema && selectedId != null && (
        <div className="flex gap-2 items-end mb-2 p-2 rounded-lg border border-white/10 bg-white/[.03]">
          <div className="flex-1">
            <label className="text-[10px] text-white/40 block mb-1">Título (requerido)</label>
            <input value={metaTitle} onChange={e => setMetaTitle(e.target.value)} placeholder="Título del tile…"
              className="w-full px-2 py-1.5 rounded-md bg-white/[.06] text-white text-xs border border-white/10 focus:border-[var(--acc1)] outline-none" />
          </div>
          <button onClick={confirmWithMeta} disabled={!metaTitle.trim()}
            className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium shrink-0 disabled:opacity-30 transition-colors"
            style={{ background: 'rgba(var(--acc1-rgb),.15)', color: 'var(--acc1)' }}>
            <CheckCircle2 size={12} />Asignar
          </button>
          <button onClick={() => setSelectedId(null)} className="text-white/30 hover:text-white/50 text-xs px-2 py-1.5">Cancelar</button>
        </div>
      )}

      {compatible.length > 0 ? (
        <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 gap-1.5 max-h-[200px] overflow-y-auto">
          {compatible.map(m => (
            <button key={m.id} onClick={() => doAssign(m.id)}
              className={`relative rounded-md overflow-hidden border bg-white/[.03] aspect-square hover:border-[var(--acc1)] hover:scale-[1.03] transition-all ${selectedId === m.id ? 'border-[var(--acc1)] ring-1 ring-[var(--acc1)]' : 'border-white/10'}`}>
              {m.media_type === 'image' ? (
                <img src={m.file} alt={m.alt_text} className="w-full h-full object-cover" loading="lazy" />
              ) : (
                <video src={m.file} className="w-full h-full object-cover" muted />
              )}
            </button>
          ))}
        </div>
      ) : (
        <p className="text-[10px] text-white/20 text-center py-3">No hay archivos compatibles en la biblioteca</p>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   CATEGORIES PANEL — full CRUD
   ═══════════════════════════════════════════════════════════════ */

const CAT_BLANK: Partial<Category> = { name: '', description: '', active: true };

function CategoriesPanel({ categories, notify, refresh }: { categories: Category[]; notify: (t: any, m: string) => void; refresh: () => Promise<void> }) {
  const [modal, setModal] = useState<'create' | 'edit' | null>(null);
  const [editing, setEditing] = useState<Partial<Category>>(CAT_BLANK);
  const [saving, setSaving] = useState(false);

  const openCreate = () => { setEditing({ ...CAT_BLANK }); setModal('create'); };
  const openEdit = (c: Category) => { setEditing({ ...c }); setModal('edit'); };
  const close = () => { setModal(null); };

  const handleSave = async () => {
    if (!editing.name?.trim()) { notify('error', 'El nombre es obligatorio'); return; }
    try { setSaving(true);
      const body = { ...editing, slug: editing.slug || slugify(editing.name!) };
      if (modal === 'create') await createCategory(body); else if (editing.id) await updateCategory(editing.id, body);
      notify('success', modal === 'create' ? 'Categoría creada' : 'Categoría actualizada'); close(); await refresh();
    } catch (e: any) { notify('error', e.message); } finally { setSaving(false); }
  };
  const handleDel = async (id: number, name: string) => {
    if (!confirm(`¿Eliminar "${name}"?`)) return;
    try { await deleteCategory(id); notify('success', 'Eliminada'); await refresh(); } catch (e: any) { notify('error', e.message); }
  };
  const set = (k: string, v: any) => setEditing((p) => ({ ...p, [k]: v }));

  return (
    <>
      <div className="sticky top-0 z-10 flex justify-between items-center mb-4 py-2 -mx-1 px-1" style={{ background: 'linear-gradient(180deg, var(--bg-dark) 60%, transparent)' }}>
        <h2 className="text-white/60 text-sm">{categories.length} categorías</h2>
        <ActionBtn icon={<Plus size={14} />} label="Nueva Categoría" onClick={openCreate} variant="accent" />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
        {categories.map((c) => (
          <div key={c.id} className="o-card hover:translate-y-[-1px] transition-transform group">
            <div className="flex items-center gap-2 mb-2">
              <Tag size={14} style={{ color: 'var(--acc2)' }} />
              <span className="font-semibold text-white flex-1">{c.name}</span>
              <Badge variant={c.active ? 'success' : 'error'}>{c.active ? 'Activa' : 'Off'}</Badge>
            </div>
            <p className="text-xs text-white/40">{c.description}</p>
            <div className="flex items-center justify-between mt-3">
              <p className="text-[10px] text-white/20"><code>{c.slug}</code></p>
              <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <ActionBtn icon={<Pencil size={11} />} onClick={() => openEdit(c)} small />
                <ActionBtn icon={<Trash2 size={11} />} onClick={() => handleDel(c.id, c.name)} small variant="danger" />
              </div>
            </div>
          </div>
        ))}
        {categories.length === 0 && <EmptyState icon={<Tag size={32} />} text="No hay categorías" className="col-span-full" />}
      </div>
      <Modal open={!!modal} title={modal === 'create' ? 'Nueva Categoría' : `Editar: ${editing.name}`} onClose={close}>
        <Field label="Nombre *"><Input value={editing.name || ''} onChange={(e) => set('name', e.target.value)} /></Field>
        <Field label="Descripción"><Textarea value={editing.description || ''} onChange={(e) => set('description', e.target.value)} /></Field>
        <Toggle checked={!!editing.active} onChange={(v) => set('active', v)} label="Activa" />
        <SaveBtn onClick={handleSave} saving={saving} label={modal === 'create' ? 'Crear' : 'Actualizar'} />
        {modal === 'edit' && editing.id && <DangerBtn onClick={() => { handleDel(editing.id!, editing.name || ''); close(); }} label="Eliminar" />}
      </Modal>
    </>
  );
}

/* ═══════════════════════════════════════════════════════════════
   PLANS PANEL — full CRUD
   ═══════════════════════════════════════════════════════════════ */

const PLAN_BLANK: Partial<Plan> & { trip_id: number } = { trip_id: 0, name: '', description: '', active: true };

function PlansPanel({ plans, trips, notify, refresh }: { plans: Plan[]; trips: Trip[]; notify: (t: any, m: string) => void; refresh: () => Promise<void> }) {
  const [modal, setModal] = useState<'create' | 'edit' | null>(null);
  const [editing, setEditing] = useState<Partial<Plan> & { trip_id: number }>(PLAN_BLANK);
  const [saving, setSaving] = useState(false);
  const [expandedPlan, setExpandedPlan] = useState<number | null>(null);

  const tripMap = useMemo(() => new Map(trips.map((t) => [t.id, t])), [trips]);
  const grouped = useMemo(() => { const g: Record<number, Plan[]> = {}; plans.forEach((p) => { (g[p.trip_id] = g[p.trip_id] || []).push(p); }); return g; }, [plans]);

  const openCreate = () => { setEditing({ ...PLAN_BLANK, trip_id: trips[0]?.id || 0 }); setModal('create'); };
  const openEdit = (p: Plan) => { setEditing({ ...p }); setModal('edit'); };
  const close = () => setModal(null);

  const handleSave = async () => {
    if (!editing.name?.trim()) { notify('error', 'El nombre es obligatorio'); return; }
    try { setSaving(true);
      const body = { ...editing, slug: editing.slug || slugify(editing.name!) };
      if (modal === 'create') await createPlan(body as any); else if (editing.id) await updatePlan(editing.id, body);
      notify('success', modal === 'create' ? 'Plan creado' : 'Plan actualizado'); close(); await refresh();
    } catch (e: any) { notify('error', e.message); } finally { setSaving(false); }
  };
  const handleDel = async (id: number) => {
    if (!confirm('¿Eliminar plan?')) return;
    try { await deletePlan(id); notify('success', 'Plan eliminado'); await refresh(); } catch (e: any) { notify('error', e.message); }
  };
  const set = (k: string, v: any) => setEditing((p) => ({ ...p, [k]: v }));

  return (
    <>
      <div className="sticky top-0 z-10 flex justify-between items-center mb-4 py-2 -mx-1 px-1" style={{ background: 'linear-gradient(180deg, var(--bg-dark) 60%, transparent)' }}>
        <h2 className="text-white/60 text-sm">{plans.length} planes</h2>
        <ActionBtn icon={<Plus size={14} />} label="Nuevo Plan" onClick={openCreate} variant="accent" />
      </div>
      <div className="space-y-5">
        {Object.entries(grouped).map(([tid, pls]) => {
          const trip = tripMap.get(Number(tid));
          return (
            <div key={tid}>
              <h3 className="text-[11px] font-semibold uppercase tracking-widest mb-2 flex items-center gap-2" style={{ color: 'var(--acc1)' }}>
                <Plane size={12} />{trip?.name || `Trip #${tid}`}<span className="text-white/25 font-normal">({pls.length})</span>
              </h3>
              <div className="space-y-3">
                {pls.map((p) => {
                  const isExp = expandedPlan === p.id;
                  return (
                    <div key={p.id} className="o-card p-0 overflow-hidden">
                      <div className="flex items-center gap-3 px-5 py-4 cursor-pointer hover:bg-white/5 transition-colors" onClick={() => setExpandedPlan(isExp ? null : p.id)}>
                        {isExp ? <ChevronDown size={14} className="text-white/30 shrink-0" /> : <ChevronRight size={14} className="text-white/30 shrink-0" />}
                        <Package size={14} style={{ color: 'var(--acc2)' }} className="shrink-0" />
                        <span className="font-medium text-white flex-1">{p.name}</span>
                        <Badge variant={p.active ? 'success' : 'error'}>{p.active ? '✓' : '✗'}</Badge>
                        <div className="flex gap-1 ml-2" onClick={e => e.stopPropagation()}>
                          <ActionBtn icon={<Pencil size={11} />} onClick={() => openEdit(p)} small />
                          <ActionBtn icon={<Trash2 size={11} />} onClick={() => handleDel(p.id)} small variant="danger" />
                        </div>
                      </div>
                      {isExp && (
                        <div className="border-t border-white/5 bg-white/[.02] px-5 py-4 space-y-6">
                          {p.description && <p className="text-xs text-white/40">{p.description}</p>}
                          <PlanPricesSection planId={p.id} notify={notify} />
                          <PlanFeaturesSection planId={p.id} notify={notify} />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
        {plans.length === 0 && <EmptyState icon={<Package size={32} />} text="No hay planes" />}
      </div>
      <Modal open={!!modal} title={modal === 'create' ? 'Nuevo Plan' : `Editar: ${editing.name}`} onClose={close}>
        <Field label="Viaje *">
          <select value={editing.trip_id} onChange={(e) => set('trip_id', +e.target.value)} className="w-full px-3 py-2 text-sm rounded-lg bg-white/5 border border-white/10 text-white outline-none">
            {trips.map((t) => <option key={t.id} value={t.id} className="bg-[var(--bg-dark)]">{t.name}</option>)}
          </select>
        </Field>
        <Field label="Nombre *"><Input value={editing.name || ''} onChange={(e) => set('name', e.target.value)} /></Field>
        <Field label="Descripción"><Textarea value={editing.description || ''} onChange={(e) => set('description', e.target.value)} /></Field>
        <Toggle checked={!!editing.active} onChange={(v) => set('active', v)} label="Activo" />
        <SaveBtn onClick={handleSave} saving={saving} />
        {modal === 'edit' && editing.id && <DangerBtn onClick={() => { handleDel(editing.id!); close(); }} label="Eliminar Plan" />}
      </Modal>
    </>
  );
}

/** Plan — Prices inline CRUD */
function PlanPricesSection({ planId, notify }: { planId: number; notify: (t: any, m: string) => void }) {
  const [items, setItems] = useState<PlanPrice[]>([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ person_type: 'adult', price: '', currency: 'MXN', valid_from: '', valid_to: '' });

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await listPlanPrices(planId); setItems(Array.isArray(r) ? r : (r as any).results ?? []); }
    catch {} finally { setLoading(false); }
  }, [planId]);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!form.price) { notify('error', 'Precio obligatorio'); return; }
    try { await createPlanPrice(planId, form); notify('success', 'Precio agregado'); setForm(f => ({ ...f, price: '', valid_from: '', valid_to: '' })); await load(); }
    catch (e: any) { notify('error', e.message); }
  };
  const remove = async (id: number) => {
    try { await deletePlanPrice(planId, id); notify('success', 'Eliminado'); await load(); }
    catch (e: any) { notify('error', e.message); }
  };

  return (
    <div>
      <p className="text-[10px] uppercase tracking-widest text-white/25 font-semibold mb-2 flex items-center gap-1"><DollarSign size={10} />Precios</p>
      {loading && <p className="text-white/30 text-xs">Cargando…</p>}
      <div className="space-y-1 mb-2">
        {items.map(pr => (
          <div key={pr.id} className="flex items-center gap-2 rounded border border-white/8 bg-white/[.02] px-3 py-1.5 text-xs group">
            <Badge variant="accent">{pr.person_type}</Badge>
            <span className="text-white font-medium">${pr.price} {pr.currency}</span>
            {pr.valid_from && <span className="text-white/25">{pr.valid_from} → {pr.valid_to}</span>}
            <span className="flex-1" />
            <button onClick={() => remove(pr.id)} className="p-0.5 rounded text-red-400 hover:bg-red-500/20 opacity-0 group-hover:opacity-100 transition-all"><Trash2 size={10} /></button>
          </div>
        ))}
      </div>
      <div className="flex gap-1.5 items-end flex-wrap text-xs">
        <select value={form.person_type} onChange={e => setForm(f => ({ ...f, person_type: e.target.value }))} className="px-2 py-1.5 rounded bg-white/5 border border-white/10 text-white text-xs outline-none">
          <option value="adult" className="bg-[var(--bg-dark)]">Adulto</option>
          <option value="child" className="bg-[var(--bg-dark)]">Niño</option>
          <option value="pet" className="bg-[var(--bg-dark)]">Mascota</option>
        </select>
        <input value={form.price} onChange={e => setForm(f => ({ ...f, price: e.target.value }))} placeholder="Precio" className="w-20 px-2 py-1.5 rounded bg-white/5 border border-white/10 text-white text-xs outline-none" />
        <input value={form.currency} onChange={e => setForm(f => ({ ...f, currency: e.target.value }))} placeholder="MXN" className="w-14 px-2 py-1.5 rounded bg-white/5 border border-white/10 text-white text-xs outline-none" />
        <input type="date" value={form.valid_from} onChange={e => setForm(f => ({ ...f, valid_from: e.target.value }))} className="px-2 py-1.5 rounded bg-white/5 border border-white/10 text-white text-xs outline-none" />
        <input type="date" value={form.valid_to} onChange={e => setForm(f => ({ ...f, valid_to: e.target.value }))} className="px-2 py-1.5 rounded bg-white/5 border border-white/10 text-white text-xs outline-none" />
        <button onClick={add} className="px-2 py-1.5 rounded text-xs font-medium" style={{ background: 'rgba(var(--acc1-rgb),.15)', color: 'var(--acc1)' }}><Plus size={12} /></button>
      </div>
    </div>
  );
}

/** Sub-tab "Precios" del viaje: el admin le pone precio a la experiencia AQUÍ,
 *  sin salir a la sección de planes. El precio vive en Plan (tarifa/versión) →
 *  PlanPrice (por tipo de persona). Reutiliza PlanPricesSection. */
function SubPrices({ tripId, notify }: { tripId: number; notify: (t: any, m: string) => void }) {
  const [plans, setPlans] = useState<Plan[]>([]);
  const [loading, setLoading] = useState(true);
  const [newPlan, setNewPlan] = useState('');
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await listPlans(tripId);
      setPlans(((r as any)?.items ?? (r as any)?.results ?? (Array.isArray(r) ? r : [])) as Plan[]);
    } catch { setPlans([]); } finally { setLoading(false); }
  }, [tripId]);
  useEffect(() => { load(); }, [load]);

  const addPlan = async () => {
    if (!newPlan.trim()) { notify('error', 'Ponle nombre al plan (ej. Tarifa General)'); return; }
    setCreating(true);
    try {
      await createPlan({ trip_id: tripId, name: newPlan.trim(), active: true } as any);
      notify('success', 'Plan creado — ahora ponle precio abajo');
      setNewPlan(''); await load();
    } catch (e: any) { notify('error', e.message); } finally { setCreating(false); }
  };
  const delPlan = async (id: number) => {
    try { await deletePlan(id); notify('success', 'Plan eliminado'); await load(); }
    catch (e: any) { notify('error', e.message); }
  };

  return (
    <div className="space-y-4">
      <p className="text-xs text-white/40 leading-relaxed">
        El precio de la experiencia se define por <b className="text-white/70">plan / tarifa</b> (ej. Tarifa General,
        Niños) y por <b className="text-white/70">tipo de persona</b>. Crea al menos un plan con su precio para que la
        experiencia se pueda reservar.
      </p>

      <div className="flex gap-2 items-center">
        <input
          value={newPlan}
          onChange={e => setNewPlan(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') addPlan(); }}
          placeholder="Nombre del plan (ej. Tarifa General)"
          className="flex-1 px-3 py-2 rounded bg-white/5 border border-white/10 text-white text-sm outline-none placeholder:text-white/30"
        />
        <button
          onClick={addPlan}
          disabled={creating}
          className="px-3 py-2 rounded text-sm font-medium disabled:opacity-50 whitespace-nowrap"
          style={{ background: 'rgba(var(--acc1-rgb),.15)', color: 'var(--acc1)' }}
        >
          <Plus size={14} className="inline mr-1" />{creating ? 'Creando…' : 'Nuevo plan'}
        </button>
      </div>

      {loading && <p className="text-white/30 text-xs">Cargando…</p>}
      {!loading && plans.length === 0 && (
        <div className="text-center py-8 text-white/30 text-sm">
          Esta experiencia no tiene planes todavía. Crea uno arriba para ponerle precio.
        </div>
      )}

      {plans.map(p => (
        <div key={p.id} className="rounded-lg border border-white/8 bg-white/[.02] p-3 space-y-3">
          <div className="flex items-center gap-2">
            <Package size={14} className="text-white/40" />
            <span className="text-white font-medium text-sm">{p.name}</span>
            <span className="flex-1" />
            <button onClick={() => delPlan(p.id)} className="p-1 rounded text-red-400 hover:bg-red-500/20" title="Eliminar plan">
              <Trash2 size={12} />
            </button>
          </div>
          <PlanPricesSection planId={p.id} notify={notify} />
        </div>
      ))}
    </div>
  );
}

/** Plan — Features inline CRUD */
function PlanFeaturesSection({ planId, notify }: { planId: number; notify: (t: any, m: string) => void }) {
  const [items, setItems] = useState<PlanFeature[]>([]);
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await listPlanFeatures(planId); setItems(Array.isArray(r) ? r : (r as any).results ?? []); }
    catch {} finally { setLoading(false); }
  }, [planId]);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!text.trim()) return;
    try { await createPlanFeature(planId, { text, order: items.length + 1 }); notify('success', 'Característica agregada'); setText(''); await load(); }
    catch (e: any) { notify('error', e.message); }
  };
  const remove = async (id: number) => {
    try { await deletePlanFeature(planId, id); notify('success', 'Eliminada'); await load(); }
    catch (e: any) { notify('error', e.message); }
  };

  return (
    <div>
      <p className="text-[10px] uppercase tracking-widest text-white/25 font-semibold mb-2 flex items-center gap-1"><CheckCircle2 size={10} />Características</p>
      {loading && <p className="text-white/30 text-xs">Cargando…</p>}
      <div className="space-y-1 mb-2">
        {items.sort((a, b) => a.order - b.order).map(f => (
          <div key={f.id} className="flex items-center gap-2 rounded border border-white/8 bg-white/[.02] px-3 py-1.5 text-xs group">
            <CheckCircle2 size={10} className="text-green-400 shrink-0" />
            <span className="text-white flex-1">{f.text}</span>
            <button onClick={() => remove(f.id)} className="p-0.5 rounded text-red-400 hover:bg-red-500/20 opacity-0 group-hover:opacity-100 transition-all"><Trash2 size={10} /></button>
          </div>
        ))}
      </div>
      <div className="flex gap-1.5">
        <input value={text} onChange={e => setText(e.target.value)} placeholder="Nueva característica…" className="flex-1 px-2 py-1.5 rounded bg-white/5 border border-white/10 text-white text-xs outline-none" />
        <button onClick={add} className="px-2 py-1.5 rounded text-xs font-medium" style={{ background: 'rgba(var(--acc1-rgb),.15)', color: 'var(--acc1)' }}><Plus size={12} /></button>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   ADDONS PANEL — full CRUD
   ═══════════════════════════════════════════════════════════════ */

const ADDON_BLANK: Partial<Addon> = { name: '', description: '', category: 'other', available: true, active: true };

function AddonsPanel({ addons, notify, refresh }: { addons: Addon[]; notify: (t: any, m: string) => void; refresh: () => Promise<void> }) {
  const [modal, setModal] = useState<'create' | 'edit' | null>(null);
  const [editing, setEditing] = useState<Partial<Addon>>(ADDON_BLANK);
  const [saving, setSaving] = useState(false);
  const [expandedAddon, setExpandedAddon] = useState<number | null>(null);

  const openCreate = () => { setEditing({ ...ADDON_BLANK }); setModal('create'); };
  const openEdit = (a: Addon) => { setEditing({ ...a }); setModal('edit'); };
  const close = () => setModal(null);

  const handleSave = async () => {
    if (!editing.name?.trim()) { notify('error', 'El nombre es obligatorio'); return; }
    try { setSaving(true);
      const body = { ...editing, slug: editing.slug || slugify(editing.name!) };
      if (modal === 'create') await createAddon(body); else if (editing.id) await updateAddon(editing.id, body);
      notify('success', modal === 'create' ? 'Addon creado' : 'Addon actualizado'); close(); await refresh();
    } catch (e: any) { notify('error', e.message); } finally { setSaving(false); }
  };
  const handleDel = async (id: number) => {
    if (!confirm('¿Eliminar addon?')) return;
    try { await deleteAddon(id); notify('success', 'Eliminado'); await refresh(); } catch (e: any) { notify('error', e.message); }
  };
  const set = (k: string, v: any) => setEditing((p) => ({ ...p, [k]: v }));

  return (
    <>
      <div className="sticky top-0 z-10 flex justify-between items-center mb-4 py-2 -mx-1 px-1" style={{ background: 'linear-gradient(180deg, var(--bg-dark) 60%, transparent)' }}>
        <h2 className="text-white/60 text-sm">{addons.length} addons</h2>
        <ActionBtn icon={<Plus size={14} />} label="Nuevo Addon" onClick={openCreate} variant="accent" />
      </div>
      <div className="space-y-3">
        {addons.map((a) => {
          const isExp = expandedAddon === a.id;
          return (
            <div key={a.id} className="o-card p-0 overflow-hidden">
              <div className="flex items-center gap-3 px-5 py-4 cursor-pointer hover:bg-white/5 transition-colors" onClick={() => setExpandedAddon(isExp ? null : a.id)}>
                {isExp ? <ChevronDown size={14} className="text-white/30 shrink-0" /> : <ChevronRight size={14} className="text-white/30 shrink-0" />}
                <Star size={14} style={{ color: 'var(--acc3)' }} className="shrink-0" />
                <span className="font-medium text-white flex-1">{a.name}</span>
                {a.category && <Badge>{a.category}</Badge>}
                <Badge variant={a.active ? 'success' : 'error'}>{a.active ? 'Activo' : 'Off'}</Badge>
                {a.available && <Badge variant="info">Disponible</Badge>}
                <div className="flex gap-1 ml-2" onClick={e => e.stopPropagation()}>
                  <ActionBtn icon={<Pencil size={11} />} onClick={() => openEdit(a)} small />
                  <ActionBtn icon={<Trash2 size={11} />} onClick={() => handleDel(a.id)} small variant="danger" />
                </div>
              </div>
              {isExp && (
                <div className="border-t border-white/5 bg-white/[.02] px-5 py-4 space-y-4">
                  {a.description && <p className="text-xs text-white/40">{a.description}</p>}
                  <AddonPricesSection addonId={a.id} notify={notify} />
                </div>
              )}
            </div>
          );
        })}
        {addons.length === 0 && <EmptyState icon={<Star size={32} />} text="No hay addons" />}
      </div>
      <Modal open={!!modal} title={modal === 'create' ? 'Nuevo Addon' : `Editar: ${editing.name}`} onClose={close}>
        <Field label="Nombre *"><Input value={editing.name || ''} onChange={(e) => set('name', e.target.value)} /></Field>
        <Field label="Descripción"><Textarea value={editing.description || ''} onChange={(e) => set('description', e.target.value)} /></Field>
        <Field label="Categoría"><Input value={editing.category || ''} onChange={(e) => set('category', e.target.value)} placeholder="Alimentación, Equipo…" /></Field>
        <div className="flex gap-6">
          <Toggle checked={!!editing.active} onChange={(v) => set('active', v)} label="Activo" />
          <Toggle checked={!!editing.available} onChange={(v) => set('available', v)} label="Disponible" />
        </div>
        <SaveBtn onClick={handleSave} saving={saving} />
        {modal === 'edit' && editing.id && <DangerBtn onClick={() => { handleDel(editing.id!); close(); }} label="Eliminar Addon" />}
      </Modal>
    </>
  );
}

/** Addon — Prices inline CRUD */
function AddonPricesSection({ addonId, notify }: { addonId: number; notify: (t: any, m: string) => void }) {
  const [items, setItems] = useState<AddonPrice[]>([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ price: '', currency: 'MXN', valid_from: '', valid_to: '' });

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await listAddonPrices(addonId); setItems(Array.isArray(r) ? r : (r as any).results ?? []); }
    catch {} finally { setLoading(false); }
  }, [addonId]);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!form.price) { notify('error', 'Precio obligatorio'); return; }
    try { await createAddonPrice(addonId, form); notify('success', 'Precio agregado'); setForm(f => ({ ...f, price: '', valid_from: '', valid_to: '' })); await load(); }
    catch (e: any) { notify('error', e.message); }
  };
  const remove = async (id: number) => {
    try { await deleteAddonPrice(addonId, id); notify('success', 'Eliminado'); await load(); }
    catch (e: any) { notify('error', e.message); }
  };

  return (
    <div>
      <p className="text-[10px] uppercase tracking-widest text-white/25 font-semibold mb-2 flex items-center gap-1"><DollarSign size={10} />Precios</p>
      {loading && <p className="text-white/30 text-xs">Cargando…</p>}
      <div className="space-y-1 mb-2">
        {items.map(pr => (
          <div key={pr.id} className="flex items-center gap-2 rounded border border-white/8 bg-white/[.02] px-3 py-1.5 text-xs group">
            <span className="text-white font-medium">${pr.price} {pr.currency}</span>
            {pr.valid_from && <span className="text-white/25">{pr.valid_from} → {pr.valid_to}</span>}
            <span className="flex-1" />
            <button onClick={() => remove(pr.id)} className="p-0.5 rounded text-red-400 hover:bg-red-500/20 opacity-0 group-hover:opacity-100 transition-all"><Trash2 size={10} /></button>
          </div>
        ))}
      </div>
      <div className="flex gap-1.5 items-end flex-wrap text-xs">
        <input value={form.price} onChange={e => setForm(f => ({ ...f, price: e.target.value }))} placeholder="Precio" className="w-20 px-2 py-1.5 rounded bg-white/5 border border-white/10 text-white text-xs outline-none" />
        <input value={form.currency} onChange={e => setForm(f => ({ ...f, currency: e.target.value }))} placeholder="MXN" className="w-14 px-2 py-1.5 rounded bg-white/5 border border-white/10 text-white text-xs outline-none" />
        <input type="date" value={form.valid_from} onChange={e => setForm(f => ({ ...f, valid_from: e.target.value }))} className="px-2 py-1.5 rounded bg-white/5 border border-white/10 text-white text-xs outline-none" />
        <input type="date" value={form.valid_to} onChange={e => setForm(f => ({ ...f, valid_to: e.target.value }))} className="px-2 py-1.5 rounded bg-white/5 border border-white/10 text-white text-xs outline-none" />
        <button onClick={add} className="px-2 py-1.5 rounded text-xs font-medium" style={{ background: 'rgba(var(--acc1-rgb),.15)', color: 'var(--acc1)' }}><Plus size={12} /></button>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   BLOGS PANEL — full CRUD
   ═══════════════════════════════════════════════════════════════ */

const BLOG_BLANK: Partial<BlogPost> = { trip_id: 0, author_id: 0, image_id: null, og_image_url: '', meta_title: '', meta_description: '', title: '', abstract: '', content: '', status: 'draft', active: true, tags: [] };

function BlogsPanel({ blogs, trips, authors, notify, refresh }: { blogs: BlogPost[]; trips: Trip[]; authors: Author[]; notify: (t: any, m: string) => void; refresh: () => Promise<void> }) {
  const [modal, setModal] = useState<'create' | 'edit' | null>(null);
  const [editing, setEditing] = useState<Partial<BlogPost>>(BLOG_BLANK);
  const [saving, setSaving] = useState(false);
  const [tagInput, setTagInput] = useState('');
  // Imágenes del viaje seleccionado (el backend exige que la imagen del
  // blog pertenezca al mismo trip — validación en Blog.clean()).
  const [tripMedia, setTripMedia] = useState<MediaItem[]>([]);
  const [mediaLoading, setMediaLoading] = useState(false);
  // Galería de la org: para elegir portada SIN pegar URLs.
  const [gallery, setGallery] = useState<GalleryImg[]>([]);
  const [uploadingImg, setUploadingImg] = useState(false);
  const imgInputRef = useRef<HTMLInputElement>(null);

  const loadTripMedia = useCallback(async (tripId: number | undefined) => {
    if (!tripId) { setTripMedia([]); return; }
    setMediaLoading(true);
    try {
      const items = await listTripMedia(tripId);
      setTripMedia((items || []).filter((m) => m.media_type === 'image'));
    } catch { setTripMedia([]); }
    finally { setMediaLoading(false); }
  }, []);

  useEffect(() => { if (modal) loadTripMedia(editing.trip_id); }, [modal, editing.trip_id, loadTripMedia]);
  useEffect(() => { if (modal) listGalleryImages().then(setGallery).catch(() => setGallery([])); }, [modal]);

  const openCreate = () => { setEditing({ ...BLOG_BLANK, trip_id: trips[0]?.id || 0, author_id: authors[0]?.id || 0 }); setTagInput(''); setModal('create'); };
  // El artículo COMPLETO se pide por id al abrir: la lista puede venir sin el
  // cuerpo (`content`) para no cargar todos los textos. Editar desde el objeto de
  // la lista y guardar borraría el contenido real.
  const openEdit = async (b: BlogPost) => {
    try {
      const full = await getBlog(b.id);
      setEditing({ ...full }); setTagInput((full.tags || []).join(', ')); setModal('edit');
    } catch (e: any) { notify('error', e?.message || 'No se pudo abrir el artículo'); }
  };
  const close = () => setModal(null);

  const selectCover = (m: MediaItem) => {
    setEditing((p) => ({
      ...p,
      image_id: p.image_id === m.id ? null : m.id,
      // auto-fill og:image con la portada si no hay uno manual distinto
      og_image_url: p.image_id === m.id ? p.og_image_url : (p.og_image_url && p.og_image_url !== m.file ? p.og_image_url : m.file),
    }));
  };

  const insertInContent = (m: MediaItem) => {
    const alt = (m.alt_text || m.filename || 'imagen').replace(/[[\]]/g, '');
    setEditing((p) => ({ ...p, content: `${p.content || ''}\n\n![${alt}](${m.file})\n` }));
    notify('success', 'Imagen insertada al final del contenido');
  };

  const handleImgUpload = async (file: File) => {
    if (!editing.trip_id) { notify('error', 'Selecciona primero el viaje'); return; }
    setUploadingImg(true);
    try {
      const res = await uploadTripMedia(editing.trip_id, file);
      const up = res?.uploaded?.[0];
      await loadTripMedia(editing.trip_id);
      if (up) setEditing((p) => ({ ...p, image_id: up.id, og_image_url: p.og_image_url || up.file }));
      notify('success', 'Imagen subida y ligada al viaje');
    } catch (e: any) { notify('error', e.message); }
    finally { setUploadingImg(false); if (imgInputRef.current) imgInputRef.current.value = ''; }
  };

  const handleSave = async () => {
    const tags = tagInput.split(',').map((t) => t.trim()).filter(Boolean);
    const body = { ...editing, tags };
    try { setSaving(true);
      if (modal === 'create') await createBlog(body); else if (editing.id) await updateBlog(editing.id, body);
      notify('success', modal === 'create' ? 'Post creado' : 'Post actualizado'); close(); await refresh();
    } catch (e: any) { notify('error', e.message); } finally { setSaving(false); }
  };
  const handleDel = async (id: number) => {
    if (!confirm('¿Eliminar post?')) return;
    try { await deleteBlog(id); notify('success', 'Eliminado'); await refresh(); } catch (e: any) { notify('error', e.message); }
  };
  const set = (k: string, v: any) => setEditing((p) => ({ ...p, [k]: v }));

  return (
    <>
      <div className="sticky top-0 z-10 flex justify-between items-center mb-4 py-2 -mx-1 px-1" style={{ background: 'linear-gradient(180deg, var(--bg-dark) 60%, transparent)' }}>
        <h2 className="text-white/60 text-sm">{blogs.length} posts</h2>
        <ActionBtn icon={<Plus size={14} />} label="Nuevo Post" onClick={openCreate} variant="accent" />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {blogs.map((b) => (
          <div key={b.id} className="o-card hover:translate-y-[-1px] transition-transform group">
            <div className="flex items-center gap-2 mb-1">
              <BookOpen size={14} style={{ color: 'var(--acc1)' }} />
              <span className="font-medium text-white flex-1 truncate">{b.title}</span>
              <Badge variant={b.status === 'published' ? 'success' : 'warn'}>{b.status}</Badge>
            </div>
            {b.abstract && <p className="text-xs text-white/40 mb-2 line-clamp-2">{b.abstract}</p>}
            <div className="flex items-center gap-1.5 flex-wrap">{(b.tags || []).map((t, i) => <Badge key={i} variant="accent">{t}</Badge>)}</div>
            <div className="flex items-center justify-between mt-2">
              <p className="text-[10px] text-white/20">Trip: {b.trip_id} · Author: {b.author_id}</p>
              <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <ActionBtn icon={<Pencil size={11} />} onClick={() => openEdit(b)} small />
                <ActionBtn icon={<Trash2 size={11} />} onClick={() => handleDel(b.id)} small variant="danger" />
              </div>
            </div>
          </div>
        ))}
        {blogs.length === 0 && <EmptyState icon={<BookOpen size={32} />} text="No hay posts" className="col-span-full" />}
      </div>
      <Modal open={!!modal} title={modal === 'create' ? 'Nuevo Post' : `Editar: ${editing.title}`} onClose={close}>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Viaje">
            <select value={editing.trip_id || 0} onChange={(e) => set('trip_id', +e.target.value)} className="w-full px-3 py-2 text-sm rounded-lg bg-white/5 border border-white/10 text-white outline-none">
              {trips.map((t) => <option key={t.id} value={t.id} className="bg-[var(--bg-dark)]">{t.name}</option>)}
            </select>
          </Field>
          <Field label="Autor">
            <select value={editing.author_id || 0} onChange={(e) => set('author_id', +e.target.value)} className="w-full px-3 py-2 text-sm rounded-lg bg-white/5 border border-white/10 text-white outline-none">
              {authors.map((a) => <option key={a.id} value={a.id} className="bg-[var(--bg-dark)]">{a.name}</option>)}
            </select>
          </Field>
        </div>
        <Field label="Título"><Input value={editing.title || ''} onChange={(e) => set('title', e.target.value)} /></Field>
        <Field label="Resumen"><Textarea value={editing.abstract || ''} onChange={(e) => set('abstract', e.target.value)} rows={2} /></Field>
        <Field label="Contenido"><Textarea value={editing.content || ''} onChange={(e) => set('content', e.target.value)} rows={6} /></Field>

        {/* ── Imágenes: portada del viaje + insertar en contenido ── */}
        <Field label="Imagen principal" hint="debe ser una imagen del viaje seleccionado; clic = portada, + = insertarla dentro del contenido">
          <input ref={imgInputRef} type="file" accept="image/*" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) handleImgUpload(f); }} />
          {mediaLoading && <p className="text-white/30 text-xs mb-1">Cargando imágenes…</p>}
          <div className="flex gap-2 overflow-x-auto pb-2">
            {tripMedia.map((m) => (
              <div key={m.id} className="relative shrink-0 group/img">
                <button
                  type="button"
                  onClick={() => selectCover(m)}
                  title={editing.image_id === m.id ? 'Portada actual (clic para quitar)' : 'Usar como portada'}
                  className={`block rounded-lg overflow-hidden border-2 transition-all ${editing.image_id === m.id ? 'border-[var(--acc1)] shadow-[0_0_10px_rgba(var(--acc1-rgb),.4)]' : 'border-white/10 hover:border-white/30'}`}
                >
                  <img src={m.file} alt={m.alt_text} className="h-16 w-24 object-cover" loading="lazy" />
                </button>
                {editing.image_id === m.id && (
                  <span className="absolute top-1 left-1 text-[9px] px-1 rounded font-semibold" style={{ background: 'var(--acc1)', color: '#000' }}>portada</span>
                )}
                <button
                  type="button"
                  onClick={() => insertInContent(m)}
                  title="Insertar en el contenido (markdown)"
                  className="absolute bottom-1 right-1 w-5 h-5 rounded bg-black/70 text-white text-xs leading-none opacity-0 group-hover/img:opacity-100 transition-opacity"
                >+</button>
              </div>
            ))}
            <button
              type="button"
              onClick={() => imgInputRef.current?.click()}
              disabled={uploadingImg || !editing.trip_id}
              className="shrink-0 h-16 w-24 rounded-lg border border-dashed border-white/20 text-white/40 text-[10px] hover:border-white/40 hover:text-white/70 transition-colors flex flex-col items-center justify-center gap-1"
            >
              {uploadingImg ? 'Subiendo…' : (<><Plus size={14} />Subir</>)}
            </button>
            {!mediaLoading && tripMedia.length === 0 && (
              <p className="text-white/30 text-xs self-center">El viaje no tiene imágenes aún — sube la primera.</p>
            )}
          </div>
        </Field>
        {/* Portada desde la galería de la org — imágenes SELECCIONABLES, sin pegar URLs. */}
        <Field label="Portada (galería)" hint="elige una imagen de tu galería como portada — sin pegar URLs">
          {gallery.length === 0 ? (
            <p className="text-white/30 text-xs">Tu galería está vacía. Genera o sube imágenes en la sección Galería, o usa las del viaje arriba.</p>
          ) : (
            <div className="grid grid-cols-4 sm:grid-cols-6 gap-2 max-h-48 overflow-y-auto pr-1">
              {gallery.map((g) => (
                <button
                  key={g.id}
                  type="button"
                  onClick={() => setEditing((p) => ({ ...p, og_image_url: g.url, image_id: null }))}
                  title="Usar como portada"
                  className={`block rounded-lg overflow-hidden border-2 transition-all ${editing.og_image_url === g.url ? 'border-[var(--acc1)] shadow-[0_0_10px_rgba(var(--acc1-rgb),.4)]' : 'border-white/10 hover:border-white/30'}`}
                >
                  <img src={g.url} alt={g.prompt || 'imagen'} className="h-16 w-full object-cover" loading="lazy" />
                </button>
              ))}
            </div>
          )}
          {editing.og_image_url && <p className="text-white/30 text-[10px] mt-1">Portada seleccionada ✓</p>}
        </Field>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Meta título (SEO)"><Input value={editing.meta_title || ''} onChange={(e) => set('meta_title', e.target.value)} /></Field>
          <Field label="Meta descripción (SEO)"><Input value={editing.meta_description || ''} onChange={(e) => set('meta_description', e.target.value)} /></Field>
        </div>

        <Field label="Tags" hint="separados por coma"><Input value={tagInput} onChange={(e) => setTagInput(e.target.value)} placeholder="aventura, naturaleza" /></Field>
        <div className="flex gap-6">
          <Field label="Estado">
            <select value={editing.status || 'draft'} onChange={(e) => set('status', e.target.value)} className="px-3 py-2 text-sm rounded-lg bg-white/5 border border-white/10 text-white outline-none">
              <option value="draft" className="bg-[var(--bg-dark)]">Borrador</option><option value="published" className="bg-[var(--bg-dark)]">Publicado</option>
            </select>
          </Field>
          <div className="flex items-end pb-1"><Toggle checked={!!editing.active} onChange={(v) => set('active', v)} label="Activo" /></div>
        </div>
        <SaveBtn onClick={handleSave} saving={saving} />
        {modal === 'edit' && editing.id && <DangerBtn onClick={() => { handleDel(editing.id!); close(); }} label="Eliminar Post" />}
      </Modal>
    </>
  );
}

/* ═══════════════════════════════════════════════════════════════
   AUTHORS PANEL — full CRUD
   ═══════════════════════════════════════════════════════════════ */

const AUTHOR_BLANK: Partial<Author> = { name: '', role: '', bio: '', active: true };

function AuthorsPanel({ authors, notify, refresh }: { authors: Author[]; notify: (t: any, m: string) => void; refresh: () => Promise<void> }) {
  const [modal, setModal] = useState<'create' | 'edit' | null>(null);
  const [editing, setEditing] = useState<Partial<Author>>(AUTHOR_BLANK);
  const [saving, setSaving] = useState(false);

  const openCreate = () => { setEditing({ ...AUTHOR_BLANK }); setModal('create'); };
  const openEdit = (a: Author) => { setEditing({ ...a }); setModal('edit'); };
  const close = () => setModal(null);

  const handleSave = async () => {
    try { setSaving(true);
      if (modal === 'create') await createAuthor(editing); else if (editing.id) await updateAuthor(editing.id, editing);
      notify('success', modal === 'create' ? 'Autor creado' : 'Autor actualizado'); close(); await refresh();
    } catch (e: any) { notify('error', e.message); } finally { setSaving(false); }
  };
  const handleDel = async (id: number) => {
    if (!confirm('¿Eliminar autor?')) return;
    try { await deleteAuthor(id); notify('success', 'Eliminado'); await refresh(); } catch (e: any) { notify('error', e.message); }
  };
  const set = (k: string, v: any) => setEditing((p) => ({ ...p, [k]: v }));

  return (
    <>
      <div className="sticky top-0 z-10 flex justify-between items-center mb-4 py-2 -mx-1 px-1" style={{ background: 'linear-gradient(180deg, var(--bg-dark) 60%, transparent)' }}>
        <h2 className="text-white/60 text-sm">{authors.length} autores</h2>
        <ActionBtn icon={<Plus size={14} />} label="Nuevo Autor" onClick={openCreate} variant="accent" />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {authors.map((a) => (
          <div key={a.id} className="o-card hover:translate-y-[-1px] transition-transform group">
            <div className="flex items-center gap-2 mb-1">
              <UserCircle size={14} style={{ color: 'var(--acc1)' }} />
              <span className="font-medium text-white flex-1">{a.name}</span>
              <Badge variant={a.active ? 'success' : 'error'}>{a.active ? 'Activo' : 'Off'}</Badge>
            </div>
            {a.role && <p className="text-xs text-white/40">{a.role}</p>}
            {a.bio && <p className="text-[11px] text-white/30 mt-1 line-clamp-2">{a.bio}</p>}
            <div className="flex items-center justify-between mt-2">
              <p className="text-[10px] text-white/20">ID: {a.id}</p>
              <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <ActionBtn icon={<Pencil size={11} />} onClick={() => openEdit(a)} small />
                <ActionBtn icon={<Trash2 size={11} />} onClick={() => handleDel(a.id)} small variant="danger" />
              </div>
            </div>
          </div>
        ))}
        {authors.length === 0 && <EmptyState icon={<UserCircle size={32} />} text="No hay autores" className="col-span-full" />}
      </div>
      <Modal open={!!modal} title={modal === 'create' ? 'Nuevo Autor' : `Editar: ${editing.name}`} onClose={close}>
        <Field label="Nombre"><Input value={editing.name || ''} onChange={(e) => set('name', e.target.value)} /></Field>
        <Field label="Rol"><Input value={editing.role || ''} onChange={(e) => set('role', e.target.value)} placeholder="Editor, Redactor…" /></Field>
        <Field label="Bio"><Textarea value={editing.bio || ''} onChange={(e) => set('bio', e.target.value)} /></Field>
        <Toggle checked={!!editing.active} onChange={(v) => set('active', v)} label="Activo" />
        <SaveBtn onClick={handleSave} saving={saving} />
        {modal === 'edit' && editing.id && <DangerBtn onClick={() => { handleDel(editing.id!); close(); }} label="Eliminar Autor" />}
      </Modal>
    </>
  );
}

/* ═══════════════════════════════════════════════════════════════
   COMPANY PANEL — edit
   ═══════════════════════════════════════════════════════════════ */

function CompanyPanel({ company, notify, refresh }: { company: Company | null; notify: (t: any, m: string) => void; refresh: () => Promise<void> }) {
  const [editing, setEditing] = useState<Partial<Company>>({});
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const logoInputRef = useRef<HTMLInputElement | null>(null);
  const [uploadingFavicon, setUploadingFavicon] = useState(false);
  const faviconInputRef = useRef<HTMLInputElement | null>(null);
  const [compTab, setCompTab] = useState<'info' | 'email' | 'stripe' | 'whatsapp' | 'meta' | 'keys' | 'departments' | 'social' | 'contacts'>('info');
  // Etiqueta por arquetipo (gobierno → "Institución"), igual que en el panel padre.
  // CompanyPanel es un componente aparte: necesita su propia resolución de scope.
  const { archetype } = useModules();
  const orgEntityLabel = archetype === 'government' ? 'Institución' : 'Empresa';

  useEffect(() => {
    if (company) { setEditing({ ...company }); setDirty(false); }
  }, [company]);

  const set = (k: string, v: any) => { setEditing((p) => ({ ...p, [k]: v })); setDirty(true); };

  const handleSave = async () => {
    try { setSaving(true); await updateCompany(editing); audit('company_update', { fields: Object.keys(editing).join(',') }); notify('success', 'Empresa actualizada'); setDirty(false); await refresh();
    } catch (e: any) { notify('error', e.message); } finally { setSaving(false); }
  };

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      setUploadingLogo(true);
      const { logo_url } = await uploadCompanyLogo(file);
      set('logo', logo_url);
      notify('success', 'Logo subido correctamente');
      await refresh();
    } catch (err: any) { notify('error', err.message); }
    finally { setUploadingLogo(false); if (logoInputRef.current) logoInputRef.current.value = ''; }
  };

  const handleFaviconUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      setUploadingFavicon(true);
      const company = await uploadCompanyFavicon(file);
      set('favicon', company.favicon);
      notify('success', 'Favicon subido correctamente');
    } catch (err: any) { notify('error', err?.message || 'No se pudo subir el favicon'); }
    finally { setUploadingFavicon(false); if (faviconInputRef.current) faviconInputRef.current.value = ''; }
  };

  const handleFaviconDelete = async () => {
    if (!confirm('¿Eliminar el favicon? El dashboard volverá a derivarlo del logo.')) return;
    try {
      setUploadingFavicon(true);
      await deleteCompanyFavicon();
      set('favicon', undefined);
      notify('success', 'Favicon eliminado — se usará el logo');
    } catch (err: any) { notify('error', err?.message || 'No se pudo eliminar el favicon'); }
    finally { setUploadingFavicon(false); }
  };

  const handleLogoDelete = async () => {
    if (!confirm('¿Eliminar el logo de la empresa?')) return;
    try {
      setUploadingLogo(true);
      await deleteCompanyLogo();
      set('logo', undefined);
      notify('success', 'Logo eliminado');
      await refresh();
    } catch (err: any) { notify('error', err.message); }
    finally { setUploadingLogo(false); }
  };

  if (!company) return <EmptyState icon={<Building2 size={32} />} text="No se pudo cargar la empresa" />;

  const compTabs: { id: typeof compTab; label: string; icon: React.ReactNode }[] = [
    { id: 'info', label: 'Datos', icon: <Building2 size={12} /> },
    { id: 'email', label: 'Email', icon: <Mail size={12} /> },
    { id: 'stripe', label: 'Pagos', icon: <CreditCard size={12} /> },
    { id: 'whatsapp', label: 'WhatsApp', icon: <MessageSquare size={12} /> },
    { id: 'meta', label: 'Meta', icon: <Globe size={12} /> },
    { id: 'keys', label: 'API keys', icon: <Key size={12} /> },
    { id: 'departments', label: 'Departamentos', icon: <Users size={12} /> },
    { id: 'social', label: 'Redes sociales', icon: <Link2 size={12} /> },
    { id: 'contacts', label: 'Contactos', icon: <Phone size={12} /> },
  ];

  return (
    <div className="space-y-4">
      <nav className="flex gap-0.5 overflow-x-auto">
        {compTabs.map(t => (
          <button key={t.id} onClick={() => setCompTab(t.id)}
            className={`flex items-center gap-1 px-3 py-1.5 text-[11px] font-medium rounded-lg transition-all whitespace-nowrap
              ${compTab === t.id ? 'text-[var(--acc1)] bg-white/5' : 'text-white/30 hover:text-white/50'}`}>
            {t.icon}{t.label}
          </button>
        ))}
      </nav>

      {compTab === 'info' && (
        <div className="max-w-xl">
          <div className="o-card space-y-4">
            <div className="flex items-center gap-2 mb-2">
              <Building2 size={18} style={{ color: 'var(--acc3)' }} />
              <h2 className="text-lg font-bold text-white">Datos de la {orgEntityLabel}</h2>
            </div>

            {/* Logo */}
            <div className="flex items-center gap-4 p-3 rounded-lg border border-white/10 bg-white/[.02]">
              {editing.logo ? (
                <img src={editing.logo} alt="Logo" className="w-16 h-16 rounded-lg object-contain border border-white/10 bg-white/5" />
              ) : (
                <div className="w-16 h-16 rounded-lg border border-dashed border-white/20 bg-white/[.03] flex items-center justify-center">
                  <Image size={20} className="text-white/20" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-white mb-1">Logo</p>
                <p className="text-[10px] text-white/30 mb-2">{editing.logo ? 'Imagen actual en CDN' : 'Sin logo — sube una imagen PNG o JPG'}</p>
                <div className="flex items-center gap-2">
                  <input ref={logoInputRef} type="file" accept="image/*" onChange={handleLogoUpload} className="hidden" />
                  <button onClick={() => logoInputRef.current?.click()} disabled={uploadingLogo}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all"
                    style={{ background: 'rgba(var(--acc1-rgb),.12)', color: 'var(--acc1)' }}>
                    {uploadingLogo ? <Loader size={12} className="animate-spin" /> : <Upload size={12} />}
                    {uploadingLogo ? 'Subiendo…' : editing.logo ? 'Cambiar' : 'Subir'}
                  </button>
                  {editing.logo && (
                    <button onClick={handleLogoDelete} disabled={uploadingLogo}
                      className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs text-red-400 bg-red-500/10 hover:bg-red-500/20 transition-colors disabled:opacity-30">
                      <Trash2 size={11} />Eliminar
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Favicon */}
            <div className="flex items-center gap-4 p-3 rounded-lg border border-white/10 bg-white/[.02]">
              {editing.favicon ? (
                <img src={editing.favicon} alt="Favicon" className="w-16 h-16 rounded-lg object-contain border border-white/10 bg-white/5" />
              ) : (
                <div className="w-16 h-16 rounded-lg border border-dashed border-white/20 bg-white/[.03] flex items-center justify-center">
                  <Image size={20} className="text-white/20" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-white mb-1">Favicon <span className="text-[10px] font-normal text-white/40">(opcional)</span></p>
                <p className="text-[10px] text-white/30 mb-2">
                  {editing.favicon
                    ? 'Favicon custom en CDN'
                    : 'Sin favicon — el dashboard auto-recortará el logo a cuadrado. Sube PNG/ICO 64×64 si quieres uno propio.'}
                </p>
                <div className="flex items-center gap-2">
                  <input ref={faviconInputRef} type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml,image/x-icon,image/vnd.microsoft.icon" onChange={handleFaviconUpload} className="hidden" />
                  <button onClick={() => faviconInputRef.current?.click()} disabled={uploadingFavicon}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all"
                    style={{ background: 'rgba(var(--acc1-rgb),.12)', color: 'var(--acc1)' }}>
                    {uploadingFavicon ? <Loader size={12} className="animate-spin" /> : <Upload size={12} />}
                    {uploadingFavicon ? 'Subiendo…' : editing.favicon ? 'Cambiar' : 'Subir'}
                  </button>
                  {editing.favicon && (
                    <button onClick={handleFaviconDelete} disabled={uploadingFavicon}
                      className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs text-red-400 bg-red-500/10 hover:bg-red-500/20 transition-colors disabled:opacity-30">
                      <Trash2 size={11} />Eliminar
                    </button>
                  )}
                </div>
              </div>
            </div>

            <Field label="Nombre"><Input value={editing.name || ''} onChange={(e) => set('name', e.target.value)} /></Field>
            <Field label="Slogan"><Input value={editing.slogan || ''} onChange={(e) => set('slogan', e.target.value)} placeholder="Viaja, vive, repite" /></Field>
            <Field label="Descripción"><Textarea value={editing.description || ''} onChange={(e) => set('description', e.target.value)} rows={4} /></Field>
            <Field label="Website"><Input value={editing.website || ''} onChange={(e) => set('website', e.target.value)} placeholder="https://…" /></Field>
            <Field label="Teléfono"><Input value={editing.phone || ''} onChange={(e) => set('phone', e.target.value)} placeholder="+52 55 1234 5678" /></Field>
            {/* Políticas de reservación — antes vivían en la pestaña «WhatsApp» y se
                guardaban contra /whatsapp-config, que NO tiene ese campo (Ninja lo
                descartaba en silencio). Son datos de la empresa: van con «Guardar». */}
            <Field label="Políticas de reservación">
              <Textarea value={editing.reservation_policies || ''} onChange={(e) => set('reservation_policies', e.target.value)} rows={6} placeholder="Ej. Cancelaciones con más de 7 días de anticipación: reembolso del anticipo. Con menos de 7 días: sin reembolso. Cambios de fecha sujetos a disponibilidad…" />
              <p className="text-[10px] text-white/30 mt-1">Se muestran en tu sitio (/politicas) y el cliente las acepta al reservar.</p>
            </Field>

            <div className="pt-2 border-t border-white/8">
              <div className="flex items-center gap-2 mb-3">
                <Palette size={14} style={{ color: 'var(--acc2)' }} />
                <p className="text-sm font-semibold text-white">Colores de marca</p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                {([
                  ['primary_color', 'Primario'],
                  ['secondary_color', 'Secundario'],
                  ['accent_color', 'Acento'],
                  ['background_color', 'Fondo'],
                  ['text_color', 'Texto'],
                ] as [string, string][]).map(([key, label]) => (
                  <Field key={key} label={label}>
                    <div className="flex items-center gap-2">
                      <input
                        type="color"
                        value={(editing as any)[key] || '#000000'}
                        onChange={(e) => set(key, e.target.value)}
                        className="w-8 h-8 rounded border border-white/10 bg-transparent cursor-pointer shrink-0"
                      />
                      <Input
                        value={(editing as any)[key] || ''}
                        onChange={(e) => set(key, e.target.value)}
                        placeholder="#000000"
                        className="flex-1"
                      />
                    </div>
                  </Field>
                ))}
              </div>
            </div>

            {dirty && <SaveBtn onClick={handleSave} saving={saving} label="Guardar Empresa" />}
          </div>
        </div>
      )}

      {compTab === 'email' && <CompanyEmailConfig notify={notify} />}
      {compTab === 'stripe' && <CompanyStripeConfig notify={notify} />}
      {compTab === 'whatsapp' && <CompanyWhatsAppConfig notify={notify} />}
      {compTab === 'meta' && <CompanyMetaConfig notify={notify} />}
      {compTab === 'keys' && <CompanyThirdPartyKeys notify={notify} />}
      {compTab === 'departments' && <CompanyDepartments notify={notify} />}
      {compTab === 'social' && <CompanySocialLinks notify={notify} />}
      {compTab === 'contacts' && <CompanyContacts notify={notify} />}
    </div>
  );
}

/** Company — Email Config (SMTP / IMAP) */
function CompanyEmailConfig({ notify }: { notify: (t: any, m: string) => void }) {
  const [cfg, setCfg] = useState<Partial<EmailConfig>>({});
  const [initialCfg, setInitialCfg] = useState<Partial<EmailConfig> | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [testing, setTesting] = useState(false);
  const [showSmtpHost, setShowSmtpHost] = useState(false);
  const [showImapHost, setShowImapHost] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await getEmailConfig();
        setCfg(r);
        setInitialCfg(r);
      } catch {}
      finally { setLoading(false); }
    })();
  }, []);

  const set = (k: string, v: any) => { setCfg(p => ({ ...p, [k]: v })); setDirty(true); };

  const save = async () => {
    try {
      setSaving(true);
      const saved = await updateEmailConfig(cfg);
      setCfg(saved);
      setInitialCfg(saved);
      notify('success', 'Config de email actualizada');
      setDirty(false);
      setShowSmtpHost(false);
      setShowImapHost(false);
    }
    catch (e: any) { notify('error', e.message); } finally { setSaving(false); }
  };

  const reset = () => {
    if (!initialCfg) return;
    setCfg(initialCfg);
    setDirty(false);
    setShowSmtpHost(false);
    setShowImapHost(false);
    notify('info', 'Valores restaurados');
  };

  const sendTest = async () => {
    if (!testEmail.trim()) { notify('error', 'Ingresa un email destino'); return; }
    try { setTesting(true); const r = await testEmailConfig(testEmail); notify('success', r.message); }
    catch (e: any) { notify('error', e.message); } finally { setTesting(false); }
  };

  if (loading) return <div className="flex items-center gap-2 py-8 justify-center text-white/30 text-sm"><Loader size={14} className="animate-spin" />Cargando…</div>;

  return (
    <div className="max-w-xl space-y-4">
      <div className="o-card space-y-4">
        <div className="flex items-center gap-2 mb-2">
          <Mail size={18} style={{ color: 'var(--acc1)' }} />
          <h2 className="text-lg font-bold text-white">SMTP (enviar correos)</h2>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Host">
            <div className="flex gap-2">
              <Input
                value={showSmtpHost || !cfg.smtp_host ? (cfg.smtp_host || '') : ''}
                onChange={e => set('smtp_host', e.target.value)}
                placeholder={cfg.smtp_host ? 'Host configurado' : 'smtp.example.com'}
                disabled={!showSmtpHost && !!cfg.smtp_host}
              />
              {!!cfg.smtp_host && (
                <button
                  type="button"
                  onClick={() => setShowSmtpHost(v => !v)}
                  className="o-btn-ghost px-3 text-xs whitespace-nowrap"
                >
                  {showSmtpHost ? 'Ocultar' : 'Editar'}
                </button>
              )}
            </div>
          </Field>
          <Field label="Puerto"><Input type="number" value={cfg.smtp_port ?? ''} onChange={e => set('smtp_port', +e.target.value)} placeholder="465" /></Field>
        </div>
        <Field label="Usuario"><Input value={cfg.smtp_user || ''} onChange={e => set('smtp_user', e.target.value)} /></Field>
        <Field label="Password"><Input type="password" value={cfg.smtp_password ?? ''} onChange={e => set('smtp_password', e.target.value)} placeholder="Sin cambios" /></Field>
        <Field label="From Email"><Input value={cfg.smtp_from_email || ''} onChange={e => set('smtp_from_email', e.target.value)} /></Field>
        <div className="flex gap-4">
          <label className="flex items-center gap-2 text-white/60 text-xs cursor-pointer">
            <input type="checkbox" checked={cfg.smtp_use_ssl ?? false} onChange={e => { set('smtp_use_ssl', e.target.checked); if (e.target.checked) set('smtp_use_tls', false); }} className="accent-[var(--acc1)]" /> SSL
          </label>
          <label className="flex items-center gap-2 text-white/60 text-xs cursor-pointer">
            <input type="checkbox" checked={cfg.smtp_use_tls ?? false} onChange={e => { set('smtp_use_tls', e.target.checked); if (e.target.checked) set('smtp_use_ssl', false); }} className="accent-[var(--acc1)]" /> TLS
          </label>
        </div>
      </div>

      <div className="o-card space-y-4">
        <div className="flex items-center gap-2 mb-2">
          <Mail size={18} style={{ color: 'var(--acc2)' }} />
          <h2 className="text-lg font-bold text-white">IMAP (leer correos)</h2>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Host">
            <div className="flex gap-2">
              <Input
                value={showImapHost || !cfg.imap_host ? (cfg.imap_host || '') : ''}
                onChange={e => set('imap_host', e.target.value)}
                placeholder={cfg.imap_host ? 'Host configurado' : 'imap.example.com'}
                disabled={!showImapHost && !!cfg.imap_host}
              />
              {!!cfg.imap_host && (
                <button
                  type="button"
                  onClick={() => setShowImapHost(v => !v)}
                  className="o-btn-ghost px-3 text-xs whitespace-nowrap"
                >
                  {showImapHost ? 'Ocultar' : 'Editar'}
                </button>
              )}
            </div>
          </Field>
          <Field label="Puerto"><Input type="number" value={cfg.imap_port ?? ''} onChange={e => set('imap_port', +e.target.value)} placeholder="993" /></Field>
        </div>
        <Field label="Usuario"><Input value={cfg.imap_user || ''} onChange={e => set('imap_user', e.target.value)} /></Field>
        <Field label="Password"><Input type="password" value={cfg.imap_password ?? ''} onChange={e => set('imap_password', e.target.value)} placeholder="Sin cambios" /></Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Mailbox"><Input value={cfg.imap_mailbox || ''} onChange={e => set('imap_mailbox', e.target.value)} placeholder="INBOX" /></Field>
          <Field label="Intervalo (seg)"><Input type="number" value={cfg.imap_poll_seconds ?? ''} onChange={e => set('imap_poll_seconds', +e.target.value)} placeholder="20" /></Field>
        </div>
        <label className="flex items-center gap-2 text-white/60 text-xs cursor-pointer">
          <input type="checkbox" checked={cfg.imap_ssl ?? false} onChange={e => set('imap_ssl', e.target.checked)} className="accent-[var(--acc1)]" /> SSL
        </label>
      </div>

      {dirty && (
        <div className="flex flex-wrap gap-2">
          <button type="button" onClick={reset} className="o-btn-ghost px-4 py-2 text-sm">
            Restaurar
          </button>
          <SaveBtn onClick={save} saving={saving} label="Guardar Config Email" />
        </div>
      )}

      <div className="o-card space-y-3">
        <p className="text-xs text-white/40 font-semibold uppercase tracking-widest">Enviar correo de prueba</p>
        <div className="flex gap-2">
          <Input value={testEmail} onChange={e => setTestEmail(e.target.value)} placeholder="admin@ejemplo.com" className="flex-1" />
          <button onClick={sendTest} disabled={testing} className="flex items-center gap-1 px-3 py-2 rounded-lg text-xs font-medium transition-colors shrink-0" style={{ background: 'rgba(var(--acc1-rgb),.15)', color: 'var(--acc1)' }}>
            {testing ? <Loader size={12} className="animate-spin" /> : <Send size={12} />} Enviar prueba
          </button>
        </div>
      </div>
    </div>
  );
}

/** Company — Stripe Config */
function CompanyStripeConfig({ notify }: { notify: (t: any, m: string) => void }) {
  const [cfg, setCfg] = useState<Partial<StripeConfig>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    (async () => {
      try { const r = await getStripeConfig(); setCfg(r); } catch {}
      finally { setLoading(false); }
    })();
  }, []);

  const set = (k: string, v: any) => { setCfg(p => ({ ...p, [k]: v })); setDirty(true); };

  const save = async () => {
    try { setSaving(true); await updateStripeConfig(cfg); notify('success', 'Config de Stripe actualizada'); setDirty(false); }
    catch (e: any) { notify('error', e.message); } finally { setSaving(false); }
  };

  if (loading) return <div className="flex items-center gap-2 py-8 justify-center text-white/30 text-sm"><Loader size={14} className="animate-spin" />Cargando…</div>;

  return (
    <div className="max-w-xl">
      <div className="o-card space-y-4">
        <div className="flex items-center gap-2 mb-2">
          <CreditCard size={18} style={{ color: 'var(--acc1)' }} />
          <h2 className="text-lg font-bold text-white">Configuración de Stripe</h2>
        </div>
        <Field label="Secret Key">
          <div className="flex items-center gap-2">
            <Input type="password" value={cfg.stripe_secret_key ?? ''} onChange={e => set('stripe_secret_key', e.target.value)} placeholder="Sin cambios" className="flex-1" />
            {cfg.stripe_secret_key_set ? <Badge variant="success">Configurada</Badge> : <Badge variant="error">No configurada</Badge>}
          </div>
        </Field>
        <Field label="Webhook Secret">
          <div className="flex items-center gap-2">
            <Input type="password" value={cfg.stripe_webhook_secret ?? ''} onChange={e => set('stripe_webhook_secret', e.target.value)} placeholder="Sin cambios" className="flex-1" />
            {cfg.stripe_webhook_secret_set ? <Badge variant="success">Configurado</Badge> : <Badge variant="error">No configurado</Badge>}
          </div>
        </Field>
        <Field label="Success URL">
          <Input value={cfg.stripe_success_url || ''} onChange={e => set('stripe_success_url', e.target.value)} placeholder="https://…/booking/success?token={token}" />
        </Field>
        <Field label="Cancel URL">
          <Input value={cfg.stripe_cancel_url || ''} onChange={e => set('stripe_cancel_url', e.target.value)} placeholder="https://…/booking/cancel?token={token}" />
        </Field>
        <p className="text-[10px] text-white/30">Usa <code className="text-white/50">{'{token}'}</code> como placeholder en las URLs. Se reemplaza automáticamente con el token de la reservación.</p>
        {dirty && <SaveBtn onClick={save} saving={saving} label="Guardar Config Stripe" />}
      </div>
    </div>
  );
}

/** Company — WhatsApp.
 *  Con la app «WhatsApp» activa (la org ya tiene número), TODO lo de WhatsApp
 *  vive en la app: aquí solo se deja el enlace. Sin app (todavía sin número),
 *  este es el lugar para conectar el primero — sin él la app no se enciende. */
function CompanyWhatsAppConfig({ notify }: { notify: (t: any, m: string) => void }) {
  const { isEnabled, refresh } = useModules();
  const appOn = (() => { try { return isEnabled('whatsapp_app'); } catch { return false; } })();
  if (appOn) return <WhatsAppMovedToApp />;
  return (
    <div className="max-w-xl">
      {/* Conectar el número de WhatsApp Business por Embedded Signup de Meta
          (coexistencia: sigue en la app del teléfono). Al conectar, se vuelven a
          pedir los módulos para que la app WhatsApp aparezca sin recargar. */}
      <div className="mb-4">
        <WhatsAppCoexistenceConnect notify={notify} onConnected={() => refresh()} />
      </div>
      <WhatsAppWelcomeSettings notify={(type, title, message) => notify(type, message ? `${title} · ${message}` : title)} />
    </div>
  );
}

function WhatsAppMovedToApp() {
  const navigate = useNavigate();
  const go = (tab: string) => (e: React.MouseEvent) => { e.preventDefault(); navigate(`/m/whatsapp-app/${tab}`); };
  const items: [string, string, string][] = [
    ['numeros', 'Números y conexión', 'tus números y «Conectar WhatsApp»'],
    ['ajustes', 'Configuración', 'modo de respuesta, bienvenida, perfil, catálogo y horario'],
    ['difusion', 'Difusión', 'contactos, plantillas y envíos masivos'],
  ];
  return (
    <div className="max-w-xl rounded-2xl border border-border bg-card p-5 text-card-foreground">
      <div className="flex items-center gap-3">
        <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-muted"><WhatsAppGlyph size={22} /></span>
        <div className="min-w-0">
          <h2 className="text-base font-semibold text-foreground">WhatsApp ahora tiene su propia app</h2>
          <p className="text-sm text-muted-foreground">Todo lo de WhatsApp se administra desde ahí.</p>
        </div>
      </div>
      <ul className="mt-4 space-y-2">
        {items.map(([tab, label, sub]) => (
          <li key={tab}>
            <a href={`/m/whatsapp-app/${tab}`} onClick={go(tab)}
              className="flex items-center gap-3 rounded-xl border border-border px-3 py-2.5 hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
              <span className="min-w-0 flex-1">
                <span className="block text-sm font-medium text-foreground">{label}</span>
                <span className="block text-xs text-muted-foreground">{sub}</span>
              </span>
              <ExternalLink size={14} className="shrink-0 text-muted-foreground" aria-hidden />
            </a>
          </li>
        ))}
      </ul>
      <p className="mt-3 text-xs text-muted-foreground">Las políticas de reservación ahora están en «Datos».</p>
    </div>
  );
}

/** Company — Meta Config (Facebook / Instagram / Ads / WhatsApp IDs) */
function CompanyMetaConfig({ notify }: { notify: (t: any, m: string) => void }) {
  const [cfg, setCfg] = useState<Partial<MetaConfig>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    (async () => {
      try { const r = await getMetaConfig(); setCfg(r); } catch {}
      finally { setLoading(false); }
    })();
  }, []);

  const set = (k: string, v: any) => { setCfg(p => ({ ...p, [k]: v })); setDirty(true); };

  const save = async () => {
    try { setSaving(true); await updateMetaConfig(cfg); notify('success', 'Config de Meta actualizada'); setDirty(false); }
    catch (e: any) { notify('error', e.message); } finally { setSaving(false); }
  };

  if (loading) return <div className="flex items-center gap-2 py-8 justify-center text-white/30 text-sm"><Loader size={14} className="animate-spin" />Cargando…</div>;

  return (
    <div className="max-w-xl">
      <div className="o-card space-y-4">
        <div className="flex items-center gap-2 mb-2">
          <Globe size={18} style={{ color: 'var(--acc2)' }} />
          <h2 className="text-lg font-bold text-white">Cuentas de Meta</h2>
        </div>
        <p className="text-white/40 text-xs -mt-2">Estos IDs determinan qué recursos puede ver y operar esta organización en el módulo de Meta.</p>
        <Field label="Facebook Page ID"><Input value={cfg.meta_page_id || ''} onChange={e => set('meta_page_id', e.target.value)} placeholder="123456789" /></Field>
        <Field label="Instagram Account ID"><Input value={cfg.meta_ig_account_id || ''} onChange={e => set('meta_ig_account_id', e.target.value)} placeholder="17841400000000" /></Field>
        <Field label="Ad Account ID"><Input value={cfg.meta_ad_account_id || ''} onChange={e => set('meta_ad_account_id', e.target.value)} placeholder="act_987654321" /></Field>
        <Field label="WhatsApp Business ID"><Input value={cfg.meta_waba_id || ''} onChange={e => set('meta_waba_id', e.target.value)} placeholder="111222333444" /></Field>
        <Field label="WA Phone Number ID"><Input value={cfg.meta_wa_phone_number_id || ''} onChange={e => set('meta_wa_phone_number_id', e.target.value)} placeholder="555666777888" /></Field>
        <p className="text-[10px] text-white/30">Si se dejan vacíos, se muestran todos los recursos del System User Token. Puedes encontrar los IDs en Redes → Páginas, Instagram, Ads, WhatsApp.</p>
        {dirty && <SaveBtn onClick={save} saving={saving} label="Guardar Config Meta" />}
      </div>

      {/* OAuth panel — el flow recomendado para clientes nuevos. Los IDs
          de arriba quedan como override manual / legacy. */}
      <div className="mt-4">
        <MetaConnectPanel notify={notify} />
      </div>
    </div>
  );
}

/** Company — Third-party API keys (HeyGen, ElevenLabs, fal, Remotion) */
function CompanyThirdPartyKeys({ notify }: { notify: (t: any, m: string) => void }) {
  const [cfg, setCfg] = useState<ThirdPartyKeys | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  // inputs — null = 'sin cambios'; '' = 'borrar'; string = 'reemplazar'
  const [heygen, setHeygen] = useState<string | null>(null);
  const [eleven, setEleven] = useState<string | null>(null);
  const [fal, setFal] = useState<string | null>(null);
  const [remotionUrl, setRemotionUrl] = useState<string | null>(null);
  const [remotionTok, setRemotionTok] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await getThirdPartyKeys();
      setCfg(r);
      setHeygen(null); setEleven(null); setFal(null);
      setRemotionUrl(null); setRemotionTok(null);
    } catch (e: any) { notify('error', e.message); }
    finally { setLoading(false); }
  }, [notify]);

  useEffect(() => { load(); }, [load]);

  const dirty =
    heygen !== null || eleven !== null || fal !== null
    || remotionUrl !== null || remotionTok !== null;

  const save = async () => {
    setSaving(true);
    try {
      const body: Record<string, string> = {};
      if (heygen !== null) body.heygen_api_key = heygen;
      if (eleven !== null) body.elevenlabs_api_key = eleven;
      if (fal !== null) body.fal_api_key = fal;
      if (remotionUrl !== null) body.remotion_renderer_url = remotionUrl;
      if (remotionTok !== null) body.remotion_api_token = remotionTok;
      await updateThirdPartyKeys(body);
      notify('success', 'API keys actualizadas');
      await load();
    } catch (e: any) { notify('error', e.message); }
    finally { setSaving(false); }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 py-8 justify-center text-white/30 text-sm">
        <Loader size={14} className="animate-spin" /> Cargando…
      </div>
    );
  }
  if (!cfg) return null;

  return (
    <div className="max-w-xl space-y-4">
      <div className="o-card space-y-4">
        <div className="flex items-center gap-2 mb-1">
          <Key size={18} style={{ color: 'var(--acc2)' }} />
          <h2 className="text-lg font-bold text-white">API keys de terceros</h2>
        </div>
        <p className="text-white/40 text-xs -mt-1">
          Estas credenciales se guardan encriptadas por organización. Vacías =
          usa la variable de entorno global como fallback. Los valores actuales
          nunca se exponen en texto plano; solo mostramos si están configurados
          o no.
        </p>

        <SecretField
          label="HeyGen API key"
          placeholder="hk_..."
          isSet={cfg.heygen_api_key_set}
          value={heygen}
          onChange={setHeygen}
        />
        <SecretField
          label="ElevenLabs API key"
          placeholder="sk_..."
          isSet={cfg.elevenlabs_api_key_set}
          value={eleven}
          onChange={setEleven}
        />
        <SecretField
          label="fal.ai API key"
          placeholder="fal-..."
          isSet={cfg.fal_api_key_set}
          value={fal}
          onChange={setFal}
        />

        <Field label="Remotion renderer URL">
          <Input
            value={remotionUrl ?? cfg.remotion_renderer_url}
            onChange={(e) => setRemotionUrl(e.target.value)}
            placeholder="https://remotion.mi-dominio.com"
          />
        </Field>
        <SecretField
          label="Remotion API token"
          placeholder="(opcional)"
          isSet={cfg.remotion_api_token_set}
          value={remotionTok}
          onChange={setRemotionTok}
        />

        <p className="text-[10px] text-white/30">
          💡 Cuando una key está configurada aquí, el backend la usa en lugar
          del .env. Útil para que cada org de la agencia tenga su propia
          cuenta / cargos.
        </p>

        {dirty && <SaveBtn onClick={save} saving={saving} label="Guardar API keys" />}
      </div>

      {/* TKT-97 — Modelos LLM (Anthropic, OpenAI, Google, etc.). Sistema
          separado del bloque de arriba (HEYGEN/ELEVENLABS/FAL son Company
          fields; los LLM viven en su propia tabla OrgLLMProvider con
          metadata extra: default_model, last_tested_at, is_active). */}
      <LLMProvidersSection />
    </div>
  );
}

/** Input reusable para secretos — muestra estado (•••• set / empty) y
 *  permite editar o borrar. */
function SecretField({
  label, placeholder, isSet, value, onChange,
}: {
  label: string;
  placeholder?: string;
  isSet: boolean;
  value: string | null;
  onChange: (v: string | null) => void;
}) {
  const editing = value !== null;
  return (
    <Field label={label}>
      {editing ? (
        <div className="flex gap-1.5 items-center">
          <Input
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder}
            type="password"
          />
          <button
            type="button"
            onClick={() => onChange(null)}
            title="Descartar cambio"
            className="px-2 py-1.5 text-[11px] rounded-md bg-white/5 hover:bg-white/10 text-white/60"
          >
            <X size={12} />
          </button>
        </div>
      ) : (
        <div className="flex gap-1.5 items-center">
          <div className="flex-1 px-3 py-2 text-xs rounded-md bg-white/5 border border-white/10 text-white/60">
            {isSet ? '•••••••••• configurada' : 'sin configurar'}
          </div>
          <button
            type="button"
            onClick={() => onChange('')}
            title={isSet ? 'Reemplazar' : 'Configurar'}
            className="px-2 py-1.5 text-[11px] rounded-md bg-[var(--acc1)]/15 hover:bg-[var(--acc1)]/25 text-[var(--acc1)] flex items-center gap-1"
          >
            {isSet ? <><Shuffle size={11} /> Rotar</> : <><Plus size={11} /> Añadir</>}
          </button>
          {isSet && (
            <button
              type="button"
              onClick={() => onChange('')}
              title="Borrar"
              className="px-2 py-1.5 text-[11px] rounded-md bg-white/5 hover:bg-red-500/20 text-red-400"
            >
              <Eraser size={11} />
            </button>
          )}
        </div>
      )}
    </Field>
  );
}

/** Company — Departments CRUD */
function CompanyDepartments({ notify }: { notify: (t: any, m: string) => void }) {
  const [items, setItems] = useState<Department[]>([]);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await listDepartments(); setItems(Array.isArray(r) ? r : (r as any).results ?? []); }
    catch {} finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!name.trim()) return;
    try { await createDepartment({ name }); notify('success', 'Departamento creado'); setName(''); await load(); }
    catch (e: any) { notify('error', e.message); }
  };
  const remove = async (id: number) => {
    if (!confirm('¿Eliminar departamento?')) return;
    try { await deleteDepartment(id); notify('success', 'Eliminado'); await load(); }
    catch (e: any) { notify('error', e.message); }
  };

  return (
    <div className="max-w-xl space-y-3">
      {loading && <p className="text-white/30 text-sm flex items-center gap-2"><Loader size={14} className="animate-spin" />Cargando…</p>}
      {items.map(d => (
        <div key={d.id} className="flex items-center gap-2 rounded-lg border border-white/8 bg-white/[.03] px-4 py-3 group">
          <Users size={14} className="text-[var(--acc2)] shrink-0" />
          <span className="flex-1 text-white text-sm font-medium">{d.name}</span>
          <button onClick={() => remove(d.id)} className="p-1 rounded text-red-400 hover:bg-red-500/20 opacity-0 group-hover:opacity-100 transition-all"><Trash2 size={12} /></button>
        </div>
      ))}
      {!loading && items.length === 0 && <EmptyState icon={<Users size={24} />} text="Sin departamentos" />}
      <div className="flex gap-2">
        <div className="flex-1"><Input value={name} onChange={e => setName(e.target.value)} placeholder="Nombre del departamento…" /></div>
        <button onClick={add} className="flex items-center gap-1 px-3 py-2 rounded-lg text-xs font-medium transition-colors shrink-0" style={{ background: 'rgba(var(--acc1-rgb),.15)', color: 'var(--acc1)' }}><Plus size={14} />Agregar</button>
      </div>
    </div>
  );
}

/** Company — Social Links CRUD */
function CompanySocialLinks({ notify }: { notify: (t: any, m: string) => void }) {
  const [items, setItems] = useState<SocialLink[]>([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ platform: '', url: '' });

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await listSocialLinks(); setItems(Array.isArray(r) ? r : (r as any).results ?? []); }
    catch {} finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!form.platform.trim() || !form.url.trim()) { notify('error', 'Plataforma y URL obligatorios'); return; }
    try { await createSocialLink(form); notify('success', 'Red social agregada'); setForm({ platform: '', url: '' }); await load(); }
    catch (e: any) { notify('error', e.message); }
  };
  const remove = async (id: number) => {
    if (!confirm('¿Eliminar red social?')) return;
    try { await deleteSocialLink(id); notify('success', 'Eliminada'); await load(); }
    catch (e: any) { notify('error', e.message); }
  };

  const platformIcon = (p: string) => {
    const lc = p.toLowerCase();
    if (lc.includes('facebook') || lc.includes('instagram') || lc.includes('twitter') || lc.includes('tiktok') || lc.includes('youtube')) return <Globe size={12} />;
    if (lc.includes('mail')) return <Mail size={12} />;
    return <Link2 size={12} />;
  };

  return (
    <div className="max-w-xl space-y-3">
      {loading && <p className="text-white/30 text-sm flex items-center gap-2"><Loader size={14} className="animate-spin" />Cargando…</p>}
      {items.map(s => (
        <div key={s.id} className="flex items-center gap-2 rounded-lg border border-white/8 bg-white/[.03] px-4 py-3 group">
          {platformIcon(s.platform)}
          <span className="text-white text-sm font-medium">{s.platform}</span>
          <a href={s.url} target="_blank" rel="noopener noreferrer" className="flex-1 text-[var(--acc1)] text-xs truncate hover:underline">{s.url}</a>
          <button onClick={() => remove(s.id)} className="p-1 rounded text-red-400 hover:bg-red-500/20 opacity-0 group-hover:opacity-100 transition-all"><Trash2 size={12} /></button>
        </div>
      ))}
      {!loading && items.length === 0 && <EmptyState icon={<Globe size={24} />} text="Sin redes sociales" />}
      <div className="rounded-lg border border-dashed border-white/15 p-3 space-y-2">
        <p className="text-[10px] uppercase tracking-widest text-white/25 font-semibold">Agregar red social</p>
        <div className="flex gap-2">
          <Input value={form.platform} onChange={e => setForm(f => ({ ...f, platform: e.target.value }))} placeholder="Plataforma (ej: Instagram)" />
          <Input value={form.url} onChange={e => setForm(f => ({ ...f, url: e.target.value }))} placeholder="URL" className="flex-1" />
          <button onClick={add} className="flex items-center gap-1 px-3 py-2 rounded-lg text-xs font-medium transition-colors shrink-0" style={{ background: 'rgba(var(--acc1-rgb),.15)', color: 'var(--acc1)' }}><Plus size={14} />Agregar</button>
        </div>
      </div>
    </div>
  );
}

/** Company — Contacts CRUD */
function CompanyContacts({ notify }: { notify: (t: any, m: string) => void }) {
  const [items, setItems] = useState<CompanyContact[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ department_id: 0, type: 'email', value: '' });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [c, d] = await Promise.all([listContacts(), listDepartments().catch(() => [])]);
      setItems(Array.isArray(c) ? c : (c as any).results ?? []);
      setDepartments(Array.isArray(d) ? d : (d as any).results ?? []);
    } catch {} finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!form.value.trim()) { notify('error', 'Valor obligatorio'); return; }
    try { await createContact(form); notify('success', 'Contacto agregado'); setForm(f => ({ ...f, value: '' })); await load(); }
    catch (e: any) { notify('error', e.message); }
  };
  const remove = async (id: number) => {
    if (!confirm('¿Eliminar contacto?')) return;
    try { await deleteContact(id); notify('success', 'Eliminado'); await load(); }
    catch (e: any) { notify('error', e.message); }
  };

  const deptName = (id: number) => departments.find(d => d.id === id)?.name || `Dept #${id}`;

  return (
    <div className="max-w-xl space-y-3">
      {loading && <p className="text-white/30 text-sm flex items-center gap-2"><Loader size={14} className="animate-spin" />Cargando…</p>}
      {items.map(c => (
        <div key={c.id} className="flex items-center gap-2 rounded-lg border border-white/8 bg-white/[.03] px-4 py-3 group">
          {c.type === 'email' ? <Mail size={12} className="text-[var(--acc1)] shrink-0" /> : <Phone size={12} className="text-[var(--acc2)] shrink-0" />}
          <span className="text-white text-sm">{c.value}</span>
          <Badge>{c.type}</Badge>
          <span className="text-white/25 text-[10px]">{deptName(c.department_id)}</span>
          <span className="flex-1" />
          <button onClick={() => remove(c.id)} className="p-1 rounded text-red-400 hover:bg-red-500/20 opacity-0 group-hover:opacity-100 transition-all"><Trash2 size={12} /></button>
        </div>
      ))}
      {!loading && items.length === 0 && <EmptyState icon={<Phone size={24} />} text="Sin contactos" />}
      <div className="rounded-lg border border-dashed border-white/15 p-3 space-y-2">
        <p className="text-[10px] uppercase tracking-widest text-white/25 font-semibold">Agregar contacto</p>
        <div className="flex gap-2 items-end flex-wrap">
          {departments.length > 0 && (
            <Field label="Departamento">
              <select value={form.department_id} onChange={e => setForm(f => ({ ...f, department_id: +e.target.value }))} className="px-3 py-2 text-sm rounded-lg bg-white/5 border border-white/10 text-white outline-none">
                <option value={0} className="bg-[var(--bg-dark)]">— Sin depto —</option>
                {departments.map(d => <option key={d.id} value={d.id} className="bg-[var(--bg-dark)]">{d.name}</option>)}
              </select>
            </Field>
          )}
          <Field label="Tipo">
            <select value={form.type} onChange={e => setForm(f => ({ ...f, type: e.target.value }))} className="px-3 py-2 text-sm rounded-lg bg-white/5 border border-white/10 text-white outline-none">
              <option value="email" className="bg-[var(--bg-dark)]">Email</option>
              <option value="phone" className="bg-[var(--bg-dark)]">Teléfono</option>
            </select>
          </Field>
          <div className="flex-1 min-w-[180px]"><Field label="Valor"><Input value={form.value} onChange={e => setForm(f => ({ ...f, value: e.target.value }))} placeholder={form.type === 'email' ? 'correo@ejemplo.com' : '+52 …'} /></Field></div>
          <button onClick={add} className="flex items-center gap-1 px-3 py-2 rounded-lg text-xs font-medium transition-colors shrink-0 mb-0.5" style={{ background: 'rgba(var(--acc1-rgb),.15)', color: 'var(--acc1)' }}><Plus size={14} />Agregar</button>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   BOOKINGS DASHBOARD PANEL — KPIs, Charts & Analytics
   ═══════════════════════════════════════════════════════════════ */

const PERIOD_PRESETS: { label: string; get: () => { start_date: string; end_date: string } }[] = [
  { label: 'Últimos 7 días', get: () => { const d = new Date(); const s = new Date(d); s.setDate(d.getDate() - 7); return { start_date: fmt(s), end_date: fmt(d) }; } },
  { label: 'Últimos 30 días', get: () => { const d = new Date(); const s = new Date(d); s.setDate(d.getDate() - 30); return { start_date: fmt(s), end_date: fmt(d) }; } },
  { label: 'Este mes', get: () => { const d = new Date(); return { start_date: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`, end_date: fmt(d) }; } },
  { label: 'Este año', get: () => { const d = new Date(); return { start_date: `${d.getFullYear()}-01-01`, end_date: fmt(d) }; } },
];
function fmt(d: Date) { return d.toISOString().slice(0, 10); }

const STATUS_COLORS: Record<string, string> = {
  paid: '#34d399', pending: '#fbbf24', cancelled: '#f87171', refunded: '#94a3b8', expired: '#6b7280',
};
const PIE_COLORS = ['#34d399', '#fbbf24', '#f87171', '#94a3b8', '#6b7280'];

function BookingsDashboardPanel({ notify }: { notify: (t: any, m: string) => void }) {
  const [data, setData] = useState<BookingDashboard | null>(null);
  const [loading, setLoading] = useState(true);
  const [periodIdx, setPeriodIdx] = useState(2); // default: "Este mes"
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [useCustom, setUseCustom] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = useCustom && customStart && customEnd
        ? { start_date: customStart, end_date: customEnd }
        : PERIOD_PRESETS[periodIdx].get();
      const d = await getBookingDashboard(params);
      setData(d);
    } catch (e: any) { notify('error', e.message); }
    finally { setLoading(false); }
  }, [periodIdx, useCustom, customStart, customEnd, notify]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="flex items-center gap-2 py-16 justify-center text-white/30 text-sm"><Loader size={16} className="animate-spin" />Cargando métricas…</div>;
  if (!data) return (
    <div className="o-card p-8 text-center space-y-3">
      <BarChart3 size={40} className="mx-auto text-white/20" />
      <p className="text-white/50 text-sm">El módulo de reservaciones aún no tiene datos.</p>
      <p className="text-white/25 text-xs">Cuando se registren reservaciones, aquí verás las métricas de ingresos, clientes y viajes.</p>
    </div>
  );

  const k = data.kpis;
  const money = (v: string) => `$${parseFloat(v).toLocaleString('es-MX', { minimumFractionDigits: 0 })}`;

  return (
    <div className="space-y-6">
      {/* Period selector */}
      <div className="flex items-center gap-2 flex-wrap">
        {PERIOD_PRESETS.map((p, i) => (
          <button key={i} onClick={() => { setPeriodIdx(i); setUseCustom(false); }}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${!useCustom && periodIdx === i ? 'bg-[var(--acc1)]/15 text-[var(--acc1)] border border-[var(--acc1)]/30' : 'text-white/40 border border-white/10 hover:text-white/60 hover:border-white/20'}`}>
            {p.label}
          </button>
        ))}
        <div className="flex items-center gap-1.5 ml-auto">
          <input type="date" value={customStart} onChange={e => { setCustomStart(e.target.value); setUseCustom(true); }}
            className="px-2 py-1.5 text-xs rounded-lg bg-transparent border border-white/10 text-white outline-none" />
          <span className="text-white/20 text-xs">→</span>
          <input type="date" value={customEnd} onChange={e => { setCustomEnd(e.target.value); setUseCustom(true); }}
            className="px-2 py-1.5 text-xs rounded-lg bg-transparent border border-white/10 text-white outline-none" />
        </div>
      </div>

      {/* Period label */}
      <p className="text-white/30 text-xs">Periodo: {data.period.start_date} → {data.period.end_date}</p>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        <KpiCard icon={<DollarSign size={16} />} label="Ingresos" value={money(k.total_revenue)} color="--acc1-rgb" />
        <KpiCard icon={<CreditCard size={16} />} label="Ticket Prom." value={money(k.average_ticket)} color="--acc2-rgb" />
        <KpiCard icon={<ShoppingCart size={16} />} label="Reservaciones" value={k.total_bookings} sub={`${k.paid_bookings} pagadas`} color="--acc1-rgb" />
        <KpiCard icon={<UserCheck size={16} />} label="Clientes" value={k.unique_customers} color="--acc3-rgb" />
        <KpiCard icon={<Users size={16} />} label="Pasajeros" value={k.total_passengers} color="--acc2-rgb" />
        <KpiCard icon={<Plane size={16} />} label="Viajes activos" value={k.active_trips} sub={`${k.upcoming_trips} próximos`} color="--acc1-rgb" />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Revenue Chart */}
        <div className="lg:col-span-2 o-card p-4">
          <h3 className="text-sm font-semibold text-white/70 mb-3 flex items-center gap-2"><TrendingUp size={14} style={{ color: 'var(--acc1)' }} />Ingresos y Reservaciones</h3>
          {data.revenue_series.length > 0 ? (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={data.revenue_series} margin={{ top: 5, right: 5, bottom: 5, left: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                <XAxis dataKey="date" tick={{ fill: 'rgba(255,255,255,0.3)', fontSize: 10 }} tickFormatter={v => v.slice(5)} />
                <YAxis yAxisId="left" tick={{ fill: 'rgba(255,255,255,0.3)', fontSize: 10 }} tickFormatter={v => `$${(v / 1000).toFixed(0)}k`} />
                <YAxis yAxisId="right" orientation="right" tick={{ fill: 'rgba(255,255,255,0.3)', fontSize: 10 }} />
                <Tooltip
                  contentStyle={{ background: 'rgba(20,20,30,0.95)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, fontSize: 12 }}
                  labelStyle={{ color: 'rgba(255,255,255,0.6)' }}
                  formatter={(value: number, name: string) => [name === 'revenue' ? `$${value.toLocaleString()}` : value, name === 'revenue' ? 'Ingresos' : name === 'booking_count' ? 'Reservaciones' : 'Pagadas']}
                />
                <Bar yAxisId="left" dataKey="revenue" fill="var(--acc1)" radius={[4, 4, 0, 0]} opacity={0.8} />
                <Line yAxisId="right" dataKey="booking_count" stroke="#fbbf24" strokeWidth={2} dot={false} />
                <Line yAxisId="right" dataKey="paid_booking_count" stroke="#34d399" strokeWidth={2} dot={false} />
              </BarChart>
            </ResponsiveContainer>
          ) : <p className="text-white/20 text-xs text-center py-10">Sin datos en el periodo</p>}
        </div>

        {/* Status Donut */}
        <div className="o-card p-4">
          <h3 className="text-sm font-semibold text-white/70 mb-3 flex items-center gap-2"><Filter size={14} style={{ color: 'var(--acc2)' }} />Por Status</h3>
          {data.status_breakdown.length > 0 ? (
            <>
              <ResponsiveContainer width="100%" height={180}>
                <PieChart>
                  <Pie data={data.status_breakdown} dataKey="booking_count" nameKey="status" cx="50%" cy="50%" innerRadius={45} outerRadius={70} paddingAngle={3}>
                    {data.status_breakdown.map((entry, i) => (
                      <Cell key={entry.status} fill={STATUS_COLORS[entry.status] || PIE_COLORS[i % PIE_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={{ background: 'rgba(20,20,30,0.95)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, fontSize: 12 }} />
                </PieChart>
              </ResponsiveContainer>
              <div className="flex flex-wrap gap-2 justify-center mt-2">
                {data.status_breakdown.map(s => (
                  <span key={s.status} className="flex items-center gap-1.5 text-[10px] text-white/50">
                    <span className="w-2 h-2 rounded-full" style={{ background: STATUS_COLORS[s.status] || '#6b7280' }} />
                    {s.status} ({s.booking_count})
                  </span>
                ))}
              </div>
            </>
          ) : <p className="text-white/20 text-xs text-center py-10">Sin datos</p>}
        </div>
      </div>

      {/* Bottom Row: Top Trips + Upcoming */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Top Trips */}
        <div className="o-card p-0 overflow-hidden">
          <div className="px-4 py-3 border-b border-white/[.06] flex items-center gap-2">
            <Star size={14} style={{ color: 'var(--acc1)' }} />
            <span className="text-sm font-semibold text-white/70">Top Viajes</span>
          </div>
          {data.top_trips.length > 0 ? (
            <div className="divide-y divide-white/5">
              {data.top_trips.map((t, i) => (
                <div key={t.trip_id} className="flex items-center gap-3 px-4 py-3 hover:bg-white/[.03] transition-colors">
                  <span className="w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold shrink-0"
                    style={{ background: `rgba(var(--acc1-rgb),${0.3 - i * 0.05})`, color: 'var(--acc1)' }}>
                    {i + 1}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="text-white text-xs font-medium truncate">{t.trip_name}</p>
                    <p className="text-[10px] text-white/30">{t.booking_count} reservas · {t.total_passengers} pasajeros</p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-xs font-semibold" style={{ color: 'var(--acc1)' }}>{money(t.revenue)}</p>
                    {t.next_date && <p className="text-[10px] text-white/25">{t.next_date}</p>}
                  </div>
                </div>
              ))}
            </div>
          ) : <p className="text-white/20 text-xs text-center py-8">Sin datos</p>}
        </div>

        {/* Upcoming Trips */}
        <div className="o-card p-0 overflow-hidden">
          <div className="px-4 py-3 border-b border-white/[.06] flex items-center gap-2">
            <Calendar size={14} style={{ color: 'var(--acc2)' }} />
            <span className="text-sm font-semibold text-white/70">Próximos Viajes</span>
          </div>
          {data.upcoming_trips.length > 0 ? (
            <div className="divide-y divide-white/5">
              {data.upcoming_trips.map(t => (
                <div key={t.trip_id} className="flex items-center gap-3 px-4 py-3 hover:bg-white/[.03] transition-colors">
                  <div className="w-10 h-10 rounded-lg flex flex-col items-center justify-center shrink-0"
                    style={{ background: 'rgba(var(--acc2-rgb),.1)' }}>
                    <span className="text-[9px] text-white/30 leading-none">{new Date(t.next_date).toLocaleString('es', { month: 'short' }).toUpperCase()}</span>
                    <span className="text-sm font-bold" style={{ color: 'var(--acc2)' }}>{new Date(t.next_date).getDate()}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-white text-xs font-medium truncate">{t.trip_name}</p>
                    <p className="text-[10px] text-white/30">{t.next_date}</p>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <span className="text-xs font-semibold" style={{ color: 'var(--acc1)' }}>{t.paid_count}</span>
                    <span className="text-white/20 text-xs">/</span>
                    <span className="text-xs text-white/40">{t.booking_count}</span>
                    <span className="text-[10px] text-white/20 ml-1">pagadas</span>
                  </div>
                </div>
              ))}
            </div>
          ) : <p className="text-white/20 text-xs text-center py-8">Sin viajes próximos</p>}
        </div>
      </div>
    </div>
  );
}

function KpiCard({ icon, label, value, sub, color }: { icon: React.ReactNode; label: string; value: string | number; sub?: string; color: string }) {
  return (
    <div className="o-card p-4 flex flex-col gap-1">
      <div className="flex items-center gap-2 text-white/40">
        <div className="flex items-center justify-center w-7 h-7 rounded-lg" style={{ background: `rgba(${color.startsWith('--') ? `var(${color})` : color}, .12)`, color: color.startsWith('--') ? `rgba(var(${color}),1)` : color }}>
          {icon}
        </div>
        <span className="text-[11px] font-medium">{label}</span>
      </div>
      <p className="text-xl font-bold text-white mt-1">{value}</p>
      {sub && <p className="text-[10px] text-white/30">{sub}</p>}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   BOOKINGS PANEL — List, Detail, Actions
   ═══════════════════════════════════════════════════════════════ */

const STATUS_BADGE: Record<string, 'success' | 'warn' | 'error' | 'info' | 'default'> = {
  paid: 'success', pending: 'warn', cancelled: 'error', refunded: 'info', expired: 'default',
};
const STATUS_LABEL: Record<string, string> = {
  paid: 'Pagada', pending: 'Pendiente', cancelled: 'Cancelada', refunded: 'Reembolsada', expired: 'Expirada',
};
const PAYMENT_STATUS_BADGE: Record<string, 'success' | 'warn' | 'error' | 'info' | 'default'> = {
  fully_paid: 'success', deposit_paid: 'info', partially_paid: 'warn', awaiting_deposit: 'warn', overpaid: 'info',
};
const PAYMENT_STATUS_LABEL: Record<string, string> = {
  fully_paid: 'Pagado total', deposit_paid: 'Anticipo pagado', partially_paid: 'Pago parcial', awaiting_deposit: 'Esperando anticipo', overpaid: 'Sobrepago',
};

function BookingsPanel({ trips, notify }: { trips: Trip[]; notify: (t: any, m: string) => void }) {
  const [items, setItems] = useState<BookingListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('');
  const [filterTrip, setFilterTrip] = useState('');
  const [searchQ, setSearchQ] = useState('');
  const [offset, setOffset] = useState(0);
  const limit = 20;
  const [detail, setDetail] = useState<BookingDetail | null>(null);
  const [showDetail, setShowDetail] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  // Alta manual (venta por WhatsApp / mostrador, sin Stripe): el formulario único
  // vive en sales-desk/RegistrarVenta (🔒 single-source; también lo usa la vendedora).
  const [newOpen, setNewOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params: any = { offset, limit };
      if (filterStatus) params.status = filterStatus;
      if (filterTrip) params.trip_id = parseInt(filterTrip);
      if (searchQ.trim()) params.search = searchQ.trim();
      const data = await listBookings(params);
      setItems(Array.isArray(data?.items) ? data.items : []);
    } catch (e: any) { notify('error', e.message); }
    finally { setLoading(false); }
  }, [offset, filterStatus, filterTrip, searchQ, notify]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setOffset(0); }, [filterStatus, filterTrip, searchQ]);

  const openDetail = async (id: number) => {
    try {
      const d = await getBooking(id);
      setDetail(d);
      setShowDetail(true);
    } catch (e: any) { notify('error', e.message); }
  };

  const doAction = async (action: 'cancel' | 'refund' | 'resend', id: number) => {
    const msgs: Record<string, string> = { cancel: '¿Cancelar esta reservación?', refund: '¿Reembolsar esta reservación?', resend: '¿Reenviar email de confirmación?' };
    if (!confirm(msgs[action])) return;
    setActionLoading(true);
    try {
      let result: { message: string };
      if (action === 'cancel') result = await cancelBooking(id);
      else if (action === 'refund') result = await refundBooking(id);
      else result = await resendBookingEmail(id);
      notify('success', result.message);
      // Refresh detail & list
      const d = await getBooking(id);
      setDetail(d);
      load();
    } catch (e: any) { notify('error', e.message); }
    finally { setActionLoading(false); }
  };

  const money = (v: string) => `$${parseFloat(v).toLocaleString('es-MX', { minimumFractionDigits: 2 })}`;

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/30" />
          <input type="text" placeholder="Buscar por ID, email o nombre…" value={searchQ} onChange={e => setSearchQ(e.target.value)}
            className="w-full pl-8 pr-3 py-2 text-sm rounded-lg bg-transparent border border-white/10 text-white placeholder:text-white/30 focus:ring-1 focus:ring-[var(--acc1)] outline-none" />
        </div>
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
          className="px-3 py-2 text-sm rounded-lg bg-transparent border border-white/10 text-white outline-none appearance-none cursor-pointer">
          <option value="" className="bg-[var(--bg-dark)]">Todos los status</option>
          {['paid', 'pending', 'cancelled', 'refunded', 'expired'].map(s => (
            <option key={s} value={s} className="bg-[var(--bg-dark)]">{STATUS_LABEL[s] || s}</option>
          ))}
        </select>
        <select value={filterTrip} onChange={e => setFilterTrip(e.target.value)}
          className="px-3 py-2 text-sm rounded-lg bg-transparent border border-white/10 text-white outline-none appearance-none cursor-pointer">
          <option value="" className="bg-[var(--bg-dark)]">Todos los viajes</option>
          {trips.map(t => <option key={t.id} value={t.id} className="bg-[var(--bg-dark)]">{t.name}</option>)}
        </select>
          <ActionBtn icon={<Plus size={14} />} label="Nueva Reserva" onClick={() => setNewOpen(true)} variant="accent" />
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex items-center gap-2 py-12 justify-center text-white/30 text-sm"><Loader size={16} className="animate-spin" />Cargando…</div>
      ) : items.length === 0 ? (
        <EmptyState icon={<ShoppingCart size={32} />} text="Sin reservaciones" />
      ) : (
        <div className="o-card p-0 overflow-hidden overflow-x-auto">
          <table className="w-full text-sm text-left min-w-[700px]">
            <thead><tr className="text-white/30 text-[11px] font-medium border-b border-white/5">
              <th className="px-4 py-2.5">ID</th><th className="px-4 py-2.5">Status</th><th className="px-4 py-2.5">Viaje</th>
              <th className="px-4 py-2.5">Cliente</th><th className="px-4 py-2.5">Total</th><th className="px-4 py-2.5">Fecha viaje</th><th className="px-4 py-2.5">Creada</th>
            </tr></thead>
            <tbody className="divide-y divide-white/5">
              {items.map(b => (
                <tr key={b.id} onClick={() => openDetail(b.id)} className="hover:bg-white/5 transition-colors cursor-pointer">
                  <td className="px-4 py-2.5"><span className="text-xs font-mono" style={{ color: 'var(--acc1)' }}>#{b.id}</span></td>
                  <td className="px-4 py-2.5"><Badge variant={STATUS_BADGE[b.status] || 'default'}>{STATUS_LABEL[b.status] || b.status}</Badge></td>
                  <td className="px-4 py-2.5 text-white text-xs">{b.trip_name}</td>
                  <td className="px-4 py-2.5">
                    <p className="text-white text-xs">{b.customer_name}</p>
                    <p className="text-[10px] text-white/30">{b.customer_email}</p>
                  </td>
                  <td className="px-4 py-2.5 text-xs font-semibold text-white">{money(b.total)} <span className="text-white/30 font-normal">{b.currency}</span></td>
                  <td className="px-4 py-2.5 text-xs text-white/50">{b.date_trip}</td>
                  <td className="px-4 py-2.5 text-xs text-white/30">{new Date(b.created_at).toLocaleDateString('es')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {!loading && items.length > 0 && (
        <div className="flex items-center justify-between">
          <button disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - limit))}
            className="px-3 py-1.5 rounded-lg text-xs border border-white/10 text-white/50 hover:text-white/80 disabled:opacity-30 transition-colors">← Anterior</button>
          <span className="text-[11px] text-white/25">Mostrando {offset + 1}–{offset + items.length}</span>
          <button disabled={items.length < limit} onClick={() => setOffset(offset + limit)}
            className="px-3 py-1.5 rounded-lg text-xs border border-white/10 text-white/50 hover:text-white/80 disabled:opacity-30 transition-colors">Siguiente →</button>
        </div>
      )}

      {/* Detail Modal */}
      {showDetail && detail && (
        <Modal open={showDetail} title={`Reservación #${detail.id}`} onClose={() => setShowDetail(false)}>
          <div className="space-y-5">
            {/* Status + Actions */}
            <div className="flex items-center gap-3 flex-wrap">
              <Badge variant={STATUS_BADGE[detail.status] || 'default'}>{STATUS_LABEL[detail.status] || detail.status}</Badge>
              {detail.payment_status && (
                <Badge variant={PAYMENT_STATUS_BADGE[detail.payment_status] || 'default'}>{PAYMENT_STATUS_LABEL[detail.payment_status] || detail.payment_status}</Badge>
              )}
              {detail.status === 'pending' && (
                <button onClick={() => doAction('cancel', detail.id)} disabled={actionLoading}
                  className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs bg-red-500/15 text-red-400 hover:bg-red-500/25 transition-colors">
                  <XCircle size={12} />{actionLoading ? 'Procesando…' : 'Cancelar'}
                </button>
              )}
              {detail.status === 'paid' && (
                <>
                  <button onClick={() => doAction('refund', detail.id)} disabled={actionLoading}
                    className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs bg-amber-500/15 text-amber-400 hover:bg-amber-500/25 transition-colors">
                    <RefreshCw size={12} />{actionLoading ? 'Procesando…' : 'Reembolsar'}
                  </button>
                  <button onClick={() => doAction('resend', detail.id)} disabled={actionLoading}
                    className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs border border-white/10 text-white/50 hover:text-white/80 transition-colors">
                    <Send size={12} />Reenviar email
                  </button>
                </>
              )}
            </div>

            {/* Customer Info */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <h4 className="text-[11px] uppercase tracking-widest text-white/30 font-semibold">Cliente</h4>
                <div className="o-card p-3 space-y-1.5">
                  <p className="text-white text-sm font-medium">{detail.customer_name}</p>
                  <p className="text-xs text-white/50 flex items-center gap-1.5"><Mail size={11} />{detail.customer_email}</p>
                  {detail.customer_phone && <p className="text-xs text-white/50 flex items-center gap-1.5"><Phone size={11} />{detail.customer_phone}</p>}
                </div>
              </div>
              <div className="space-y-2">
                <h4 className="text-[11px] uppercase tracking-widest text-white/30 font-semibold">Viaje</h4>
                <div className="o-card p-3 space-y-1.5">
                  <p className="text-white text-sm font-medium">{detail.trip_name}</p>
                  <p className="text-xs text-white/50">Plan: {detail.plan_name}</p>
                  <p className="text-xs text-white/50">Fecha: {detail.date_trip}</p>
                </div>
              </div>
            </div>

            {/* Persons */}
            {detail.persons.length > 0 && (
              <div className="space-y-2">
                <h4 className="text-[11px] uppercase tracking-widest text-white/30 font-semibold">Personas</h4>
                <div className="o-card p-0 overflow-hidden">
                  <table className="w-full text-sm">
                    <thead><tr className="text-white/30 text-[11px] border-b border-white/5">
                      <th className="px-3 py-2 text-left">Tipo</th><th className="px-3 py-2 text-center">Cant.</th><th className="px-3 py-2 text-right">Unitario</th><th className="px-3 py-2 text-right">Subtotal</th>
                    </tr></thead>
                    <tbody className="divide-y divide-white/5">
                      {detail.persons.map((p, i) => (
                        <tr key={i}>
                          <td className="px-3 py-2 text-white text-xs">{p.label}</td>
                          <td className="px-3 py-2 text-center text-white/60 text-xs">{p.quantity}</td>
                          <td className="px-3 py-2 text-right text-white/50 text-xs">{money(p.unit_price)}</td>
                          <td className="px-3 py-2 text-right text-white text-xs font-medium">{money(p.subtotal)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Addons */}
            {detail.addons.length > 0 && (
              <div className="space-y-2">
                <h4 className="text-[11px] uppercase tracking-widest text-white/30 font-semibold">Addons</h4>
                <div className="o-card p-0 overflow-hidden">
                  <table className="w-full text-sm">
                    <thead><tr className="text-white/30 text-[11px] border-b border-white/5">
                      <th className="px-3 py-2 text-left">Addon</th><th className="px-3 py-2 text-center">Cant.</th><th className="px-3 py-2 text-center">Incluidos</th><th className="px-3 py-2 text-right">Subtotal</th>
                    </tr></thead>
                    <tbody className="divide-y divide-white/5">
                      {detail.addons.map((a, i) => (
                        <tr key={i}>
                          <td className="px-3 py-2 text-white text-xs">{a.addon_name} <span className="text-white/25 text-[10px]">({a.category})</span></td>
                          <td className="px-3 py-2 text-center text-white/60 text-xs">{a.quantity}</td>
                          <td className="px-3 py-2 text-center text-white/40 text-xs">{a.quantity_included}</td>
                          <td className="px-3 py-2 text-right text-white text-xs font-medium">{money(a.subtotal)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Totals */}
            <div className="o-card p-4 space-y-2">
              <div className="flex justify-between text-xs"><span className="text-white/40">Subtotal personas</span><span className="text-white/70">{money(detail.subtotal_persons)}</span></div>
              <div className="flex justify-between text-xs"><span className="text-white/40">Subtotal addons</span><span className="text-white/70">{money(detail.subtotal_addons)}</span></div>
              <div className="flex justify-between text-sm border-t border-white/10 pt-2 mt-2"><span className="text-white font-semibold">Total</span><span className="font-bold" style={{ color: 'var(--acc1)' }}>{money(detail.total)} {detail.currency}</span></div>
              {detail.amount_paid && (
                <div className="flex justify-between text-xs"><span className="text-white/40">Pagado</span><span className="text-emerald-400">{money(detail.amount_paid)}</span></div>
              )}
              {detail.amount_due && (
                <div className="flex justify-between text-xs"><span className="text-white/40">Pendiente por cobrar</span><span className="text-amber-400 font-medium">{money(detail.amount_due)}</span></div>
              )}
            </div>

            {/* Payment Info */}
            <div className="space-y-2">
              <h4 className="text-[11px] uppercase tracking-widest text-white/30 font-semibold">Pago</h4>
              <div className="o-card p-3 space-y-1.5 text-xs">
                {detail.paid_at && <p className="text-white/50">Pagado: {new Date(detail.paid_at).toLocaleString('es')}</p>}
                {detail.stripe_payment_intent_id && <p className="text-white/30 font-mono text-[10px]">PI: {detail.stripe_payment_intent_id}</p>}
                {detail.stripe_session_id && <p className="text-white/30 font-mono text-[10px]">Session: {detail.stripe_session_id}</p>}
                <p className="text-white/50">Email confirmación: {detail.confirmation_email_sent ? '✓ Enviado' : '✗ No enviado'}</p>
                <p className="text-white/30">Creada: {new Date(detail.created_at).toLocaleString('es')} · Actualizada: {new Date(detail.updated_at).toLocaleString('es')}</p>
              </div>
            </div>

            {/* Payments (multi-payment history) */}
            {detail.payments && detail.payments.length > 0 && (
              <div className="space-y-2">
                <h4 className="text-[11px] uppercase tracking-widest text-white/30 font-semibold">Historial de Pagos</h4>
                <div className="o-card p-0 overflow-hidden">
                  <table className="w-full text-sm">
                    <thead><tr className="text-white/30 text-[11px] border-b border-white/5">
                      <th className="px-3 py-2 text-left">Tipo</th><th className="px-3 py-2 text-right">Monto</th><th className="px-3 py-2 text-left">Descripción</th><th className="px-3 py-2 text-center">Estado</th><th className="px-3 py-2 text-right">Fecha</th>
                    </tr></thead>
                    <tbody className="divide-y divide-white/5">
                      {detail.payments.map(pay => (
                        <tr key={pay.id}>
                          <td className="px-3 py-2 text-white text-xs capitalize">{pay.payment_type}</td>
                          <td className="px-3 py-2 text-right text-white text-xs font-medium">{money(pay.amount)} {pay.currency}</td>
                          <td className="px-3 py-2 text-white/50 text-xs truncate max-w-[180px]" title={pay.description}>{pay.description || '—'}</td>
                          <td className="px-3 py-2 text-center">
                            <Badge variant={pay.status === 'paid' ? 'success' : pay.status === 'pending' ? 'warn' : 'default'}>{pay.status}</Badge>
                          </td>
                          <td className="px-3 py-2 text-right text-white/40 text-[10px]">{pay.paid_at ? new Date(pay.paid_at).toLocaleString('es') : '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </Modal>
      )}

      <RegistrarVenta open={newOpen} onClose={() => setNewOpen(false)} onCreated={load} isAdmin trips={trips} />
    </div>

  );
}

/* ═══════════════════════════════════════════════════════════════
   CAPABILITIES PANEL — read-only (reference)
   ═══════════════════════════════════════════════════════════════ */

function CapabilitiesPanel({ capabilities, totalCount, search, setSearch, capScope, setCapScope, scopes }: {
  capabilities: Capability[]; totalCount: number; search: string; setSearch: (s: string) => void; capScope: string; setCapScope: (s: string) => void; scopes: string[];
}) {
  const grouped = useMemo(() => { const g: Record<string, Capability[]> = {}; capabilities.forEach((c) => { const p = c.scope.split(':')[0]; (g[p] = g[p] || []).push(c); }); return g; }, [capabilities]);
  const mc: Record<string, 'info' | 'success' | 'warn' | 'error'> = { GET: 'info', POST: 'success', PUT: 'warn', PATCH: 'warn', DELETE: 'error' };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/30" />
          <input type="text" placeholder="Buscar…" value={search} onChange={(e) => setSearch(e.target.value)} className="w-full pl-8 pr-3 py-2 text-sm rounded-lg bg-transparent border border-white/10 text-white placeholder:text-white/30 focus:ring-1 focus:ring-[var(--acc1)] outline-none" />
        </div>
        <div className="relative">
          <Filter size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/30 pointer-events-none" />
          <select value={capScope} onChange={(e) => setCapScope(e.target.value)} className="pl-8 pr-8 py-2 text-sm rounded-lg bg-transparent border border-white/10 text-white outline-none appearance-none cursor-pointer">
            {scopes.map((s) => <option key={s} value={s} className="bg-[var(--bg-dark)]">{s === 'all' ? 'Todos' : s}</option>)}
          </select>
        </div>
        <span className="text-[11px] text-white/25">{capabilities.length}/{totalCount}</span>
      </div>
      {Object.entries(grouped).sort(([a], [b]) => a.localeCompare(b)).map(([prefix, caps]) => (
        <div key={prefix}>
          <h3 className="text-[11px] font-semibold uppercase tracking-widest mb-2 flex items-center gap-2" style={{ color: 'var(--acc1)' }}>
            <Shield size={12} />{prefix}<span className="text-white/25 font-normal">({caps.length})</span>
          </h3>
          <div className="o-card p-0 overflow-hidden">
            <table className="w-full text-sm text-left">
              <thead><tr className="text-white/30 text-[11px] font-medium border-b border-white/5">
                <th className="px-4 py-2.5">Nombre</th><th className="px-4 py-2.5">Método</th><th className="px-4 py-2.5 hidden md:table-cell">Path</th><th className="px-4 py-2.5 hidden lg:table-cell">Descripción</th><th className="px-4 py-2.5 text-center">Kind</th>
              </tr></thead>
              <tbody className="divide-y divide-white/5">
                {caps.map((c) => (
                  <tr key={c.id} className="hover:bg-white/5 transition-colors">
                    <td className="px-4 py-2.5"><p className="font-medium text-white text-xs">{c.summary}</p><p className="text-[10px] text-white/25 font-mono">{c.name}</p></td>
                    <td className="px-4 py-2.5"><Badge variant={mc[c.method] || 'default'}>{c.method}</Badge></td>
                    <td className="px-4 py-2.5 hidden md:table-cell font-mono text-[11px] text-white/30 max-w-[280px] truncate">{c.path}</td>
                    <td className="px-4 py-2.5 hidden lg:table-cell text-xs text-white/40 max-w-xs truncate">{c.description}</td>
                    <td className="px-4 py-2.5 text-center"><Badge variant={c.kind === 'resource' ? 'info' : 'accent'}>{c.kind}</Badge></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}
      {capabilities.length === 0 && <EmptyState icon={<Wrench size={32} />} text="Sin capabilities" />}
    </div>
  );
}

export default TravelAdmin;
